/* eslint-disable @typescript-eslint/no-unused-vars */
import { DateTime } from "luxon";
import { z } from "zod";

export enum ProductionTypes {
    Cutting = "cutting",
    Prefab = "production",
    PreAssembly = "pre_assembly",
    Assembly = "assembly",
    Purchasing = "purchasing",
    Milestone = "milestone",
    Other = "other",
    All = "all",
    PrePrefab = "pre-production",
    PrefabPreAssembly = "production-pre_assembly",
    PreAssemblyAssembly = "pre_assembly-assembly",
    PostAssembly = "post-assembly",
    Unknown = "",
}

export function transformUnknownToDateTime(v: unknown, ctx: z.RefinementCtx) {
    const dt = typeof v === "string" ? DateTime.fromISO(v) : v;

    if (DateTime.isDateTime(dt)) {
        return dt;
    }

    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unknown type for DateTime",
    });

    return z.NEVER;
}

const station_name = z.union([z.string(), z.null()]).optional();
const common_activities_ActivityGET = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    activity_category_name: z.union([z.string(), z.null()]),
    activity_template_external_id: z.string(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    batch_id: z.number().int(),
    batch_name: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    name: z.string(),
    operation_type_id: z.number().int(),
    production_type_id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    progress: z.number(),
    project_color: z.string(),
    project_id: z.number().int(),
    project_name: z.string(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    station_abbreviation: z.union([z.string(), z.null()]),
    station_id: z.union([z.number(), z.null()]),
    station_name: z.union([z.string(), z.null()]),
    time_delta: z.union([z.number(), z.null()]),
});
const Error = z.object({ message: z.string() });
const common_activities_Activity = z.object({
    activity_category_id: z.union([z.number(), z.null()]).optional(),
    activity_category_name: z.union([z.string(), z.null()]).optional(),
    activity_template_external_id: z.string().optional(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string().optional(),
    batch_id: z.number().int().optional(),
    batch_name: z.string().optional(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    id: z.number().int(),
    name: z.string(),
    operation_type_id: z.number().int().optional(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    progress: z.number().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int().optional(),
    project_name: z.string().optional(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    station_abbreviation: z.union([z.string(), z.null()]).optional(),
    station_id: z.union([z.number(), z.null()]).optional(),
    station_name: z.union([z.string(), z.null()]).optional(),
    time_delta: z.union([z.number(), z.null()]).optional(),
});
const common_activity_categories_ActivityCategoryGET = z.object({
    group_id: z.number().int(),
    id: z.number().int(),
    match_type: z.string(),
    name: z.string(),
});
const common_activity_categories_ActivityCategoryPOST = z.object({
    group_id: z.number().int(),
    id: z.number().int().optional(),
    match_type: z.string(),
    name: z.string(),
});
const common_activity_categories_ActivityCategory = z.object({
    group_id: z.number().int(),
    id: z.number().int(),
    match_type: z.string(),
    name: z.string(),
});
const common_activity_categorization_group_rules_ActivityCategorizationGroupRule = z.object({
    category_id: z.number().int().optional(),
    id: z.number().int(),
    operator: z.string(),
    params: z.string(),
    selector: z.string(),
    target_key: z.string(),
});
const common_activity_categorization_rules_ActivityCategorizationRuleGET = z.object({
    category_id: z.number().int(),
    id: z.number().int(),
    operator: z.string(),
    params: z.string(),
    selector: z.string(),
    target_key: z.string(),
});
const common_activity_categorization_rules_ActivityCategorizationRulePOST = z.object({
    category_id: z.number().int(),
    id: z.number().int().optional(),
    operator: z.string(),
    params: z.string(),
    selector: z.string(),
    target_key: z.string(),
});
const common_activity_categorization_rules_ActivityCategorizationRule = z.object({
    category_id: z.number().int(),
    id: z.number().int(),
    operator: z.string(),
    params: z.string(),
    selector: z.string(),
    target_key: z.string(),
});
const common_activity_categorization_targets_ActivityCategorizationTargetGET = z.object({
    allowed_operations: z.array(z.string()),
    label: z.string(),
    ref_key: z.union([z.string(), z.null()]),
    target_key: z.string(),
    value_type: z.enum(["BOOL", "DATETIME", "INT", "REF", "STR"]),
});
const common_activity_category_groups_ActivityCategoryGroupGET = z.object({
    id: z.number().int(),
    name: z.string(),
});
const common_activity_category_groups_ActivityCategoryGroupPOST = z.object({
    id: z.number().int().optional(),
    name: z.string(),
});
const common_activity_category_groups_ActivityCategoryGroup = z.object({
    id: z.number().int(),
    name: z.string(),
});
const common_activity_relationships_ActivityRelationshipItemGET = z.object({
    activity_external_id: z.string(),
    activity_id: z.number().int(),
    activity_name: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    offset: z.number(),
    production_type_key: z.nativeEnum(ProductionTypes),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    source_pos: z.number(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    target_pos: z.number(),
    time_delta: z.number(),
});
const common_activity_relationships_ActivityRelationshipsGET = z.object({
    predecessors: z.array(common_activity_relationships_ActivityRelationshipItemGET),
    successors: z.array(common_activity_relationships_ActivityRelationshipItemGET),
});
const common_activity_template_detail_RequiredActivityTemplates = z.object({
    activity_template_external_id: z.string().optional(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string().optional(),
    id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
});
const common_activity_template_detail_ActivityTemplateConsumableResourceRequirement = z.object({
    activity_template_id: z.number().int(),
    activity_template_name: z.string().optional(),
    id: z.number().int().optional(),
    quantity: z.number().int(),
});
const common_activity_template_detail_ActivityTemplateResourceLink = z.object({
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string().optional(),
    id: z.number().int().optional(),
    linked_activities: z
        .array(common_activity_template_detail_ActivityTemplateConsumableResourceRequirement)
        .optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    quantity: z.number().int().optional().default(1),
});
const common_labels_LabelMini = z.object({
    id: z.number().int(),
    name: z.string().optional(),
});
const common_activity_template_detail_ActivityTemplateProcessingRequirements = z.object({
    id: z.number().int().optional(),
    minutes: z.number(),
    processing_characteristic_id: z.number().int(),
    processing_characteristic_name: z.string().optional(),
});
const common_activity_template_detail_ActivityTemplateDetailPOST = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    additional_precedences: z.array(common_activity_template_detail_RequiredActivityTemplates).optional(),
    department_ext_id: z.union([z.string(), z.null()]).optional(),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int().optional(),
    input_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLink).optional(),
    labels: z.array(common_labels_LabelMini).optional(),
    main_area: z.union([z.string(), z.null()]).optional(),
    man_hours: z.number(),
    name: z.string(),
    operation_type_id: z.number().int(),
    operation_type_name: z.string().optional(),
    output_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLink).optional(),
    parent_id: z.union([z.number(), z.null()]).optional(),
    processing_requirements: z.array(common_activity_template_detail_ActivityTemplateProcessingRequirements).optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int(),
    project_name: z.string().optional(),
    sub_area: z.union([z.string(), z.null()]).optional(),
    sub_discipline: z.union([z.string(), z.null()]).optional(),
});
const common_activity_template_detail_ActivityTemplateDetail = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    additional_precedences: z.array(common_activity_template_detail_RequiredActivityTemplates).optional(),
    department_ext_id: z.union([z.string(), z.null()]).optional(),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    input_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLink).optional(),
    labels: z.array(common_labels_LabelMini).optional(),
    main_area: z.union([z.string(), z.null()]).optional(),
    man_hours: z.number(),
    name: z.string(),
    operation_type_id: z.number().int(),
    operation_type_name: z.string().optional(),
    output_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLink).optional(),
    parent_id: z.union([z.number(), z.null()]).optional(),
    processing_requirements: z.array(common_activity_template_detail_ActivityTemplateProcessingRequirements).optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int(),
    project_name: z.string().optional(),
    sub_area: z.union([z.string(), z.null()]).optional(),
    sub_discipline: z.union([z.string(), z.null()]).optional(),
});
const common_activity_template_detail_RequiredActivityTemplatesGET = z.object({
    activity_template_external_id: z.string(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
});
const common_activity_template_detail_ActivityTemplateConsumableResourceRequirementGET = z.object({
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    id: z.number().int(),
    quantity: z.number().int(),
});
const common_activity_template_detail_ActivityTemplateResourceLinkGET = z.object({
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string(),
    id: z.number().int(),
    linked_activities: z.array(common_activity_template_detail_ActivityTemplateConsumableResourceRequirementGET),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    quantity: z.number().int(),
});
const common_labels_LabelMiniGET = z.object({
    id: z.number().int(),
    name: z.string(),
});
const common_activity_template_detail_ActivityTemplateProcessingRequirementsGET = z.object({
    id: z.number().int(),
    minutes: z.number(),
    processing_characteristic_id: z.number().int(),
    processing_characteristic_name: z.string(),
});
const common_activity_template_detail_ActivityTemplateDetailGET = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    additional_precedences: z.array(common_activity_template_detail_RequiredActivityTemplatesGET),
    department_ext_id: z.union([z.string(), z.null()]),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    input_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLinkGET),
    labels: z.array(common_labels_LabelMiniGET),
    main_area: z.union([z.string(), z.null()]),
    man_hours: z.number(),
    name: z.string(),
    operation_type_id: z.number().int(),
    operation_type_name: z.string(),
    output_resource_links: z.array(common_activity_template_detail_ActivityTemplateResourceLinkGET),
    parent_id: z.union([z.number(), z.null()]),
    processing_requirements: z.array(common_activity_template_detail_ActivityTemplateProcessingRequirementsGET),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    project_color: z.string(),
    project_id: z.number().int(),
    project_name: z.string(),
    sub_area: z.union([z.string(), z.null()]),
    sub_discipline: z.union([z.string(), z.null()]),
});
const project_id = z.union([z.number(), z.null()]).optional();
const common_activity_template_input_ActivityTemplateInputItemGET = z.object({
    activity_template_id: z.union([z.number(), z.null()]),
    activity_template_name: z.union([z.string(), z.null()]),
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string(),
    id: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
    quantity: z.union([z.number(), z.null()]),
});
const common_activity_template_output_TemplateDescriptionGET = z.object({
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    id: z.number().int(),
    quantity: z.number().int(),
});
const common_activity_template_output_ActivityTemplateOutputItemGET = z.object({
    associated_activity_templates: z.array(common_activity_template_output_TemplateDescriptionGET),
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string(),
    id: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
});
const common_activity_templates_ActivityTemplateResourceLinkGET = z.object({
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string(),
    id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    quantity: z.number().int(),
});
const common_activity_templates_PrecedenceLinkGET = z.object({
    id: z.number().int(),
    required_activity_template_id: z.number().int(),
    required_activity_template_name: z.string(),
});
const common_activity_templates_ActivityTemplatesGET = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    activity_category_name: z.union([z.string(), z.null()]),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    input_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLinkGET),
    labels: z.array(common_labels_LabelMiniGET),
    man_hours: z.number(),
    name: z.string(),
    output_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLinkGET),
    parent_id: z.union([z.number(), z.null()]),
    precedences: z.array(common_activity_templates_PrecedenceLinkGET),
    production_type_id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    project_color: z.string(),
    project_id: z.number().int(),
    project_name: z.string(),
});
const apiserverfoundation_management_data_scope_FullDataScopeGET = z.object({
    id: z.number().int(),
    key: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_api_doc_ApiDocSchemaGET = z.object({}).partial();
const apiserverfoundation_authentication_authentication_resources_AuthMainSchemaGET = z.object({ login: z.string() });
const apiserverfoundation_authentication_authentication_resources_LoginSchema = z.object({
    password: z.string(),
    user: z.string(),
});
const apiserverfoundation_authentication_authentication_resources_AuthResultSchema = z.object({
    login: z.string(),
    message: z.string(),
});
const apiserverfoundation_authentication_authentication_resources_AuthResultSchemaGET = z.object({
    login: z.string(),
    message: z.string(),
});
const marshmallow_schema_Schema = z.object({}).partial();
const apiserverfoundation_authentication_oauth2_authorize_AuthorizeSchemaGET = z.object({}).partial();
const apiserverfoundation_authentication_oauth2_callback_CallbackSchemaGET = z.object({}).partial();
const apiserverfoundation_authentication_options_AuthOptionSchemaGET = z.object({
    label: z.union([z.string(), z.null()]),
    provider_type: z.enum(["PASSWORD", "SSO"]),
    url: z.union([z.string(), z.null()]),
});
const apiserverfoundation_authentication_options_OptionsSchemaGET = z.object({
    options: z.array(apiserverfoundation_authentication_options_AuthOptionSchemaGET),
});
const apiserverfoundation_authentication_password_resource_PasswordPutRequestSchema = z.object({
    new_password: z.string(),
    old_password: z.string(),
});
const apiserverfoundation_authentication_password_resource_PasswordPutResponseSchema = z
    .object({ changed: z.boolean(), message: z.string() })
    .partial();
const apiserverfoundation_authentication_status_PasswordSchemaGET = z.object({
    is_active: z.boolean(),
    is_admin: z.boolean(),
    is_external_login: z.boolean(),
    login: z.string(),
});
const common_batch_templates_with_activity_BatchActivityTemplateWithActivityGET = z.object({
    activity: z.union([common_activities_ActivityGET, z.null()]),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    img_name: z.union([z.string(), z.null()]),
    labels: z.array(common_labels_LabelMiniGET),
    man_hours: z.number(),
    name: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
});
const calendars_api_calendar_patterns_ShiftGET = z.object({
    end_day: z.number().int(),
    end_time: z.string(),
    id: z.number().int(),
    note: z.union([z.string(), z.null()]),
    start_day: z.number().int(),
    start_time: z.string(),
});
const calendars_api_calendar_patterns_CalendarPatternGET = z.object({
    id: z.number().int(),
    name: z.string(),
    periodicity: z.number().int(),
    shifts: z.array(calendars_api_calendar_patterns_ShiftGET),
});
const calendars_api_calendar_patterns_Shift = z.object({
    end_day: z.number().int(),
    end_time: z.string(),
    id: z.number().int().optional(),
    note: z.union([z.string(), z.null()]).optional().default(null),
    start_day: z.number().int(),
    start_time: z.string(),
});
const calendars_api_calendar_patterns_CalendarPatternPOST = z.object({
    id: z.number().int().optional(),
    name: z.string(),
    periodicity: z.number().int(),
    shifts: z.array(calendars_api_calendar_patterns_Shift),
});
const calendars_api_calendar_patterns_CalendarPattern = z.object({
    id: z.number().int(),
    name: z.string(),
    periodicity: z.number().int(),
    shifts: z.array(calendars_api_calendar_patterns_Shift),
});
const calendars_api_calendars_CalendarStretchGET = z.object({
    calendar_pattern_id: z.number().int(),
    calendar_pattern_name: z.string(),
    id: z.number().int(),
    note: z.union([z.string(), z.null()]),
    public_holiday_calendar_id: z.union([z.number(), z.null()]),
    public_holiday_calendar_name: z.union([z.string(), z.null()]),
    start_time: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_calendars_CalendarGET = z.object({
    id: z.number().int(),
    name: z.string(),
    stretches: z.array(calendars_api_calendars_CalendarStretchGET),
    timezone: z.string(),
});
const calendars_api_calendars_CalendarStretch = z.object({
    calendar_pattern_id: z.number().int(),
    calendar_pattern_name: z.string().optional(),
    id: z.number().int().optional(),
    note: z.union([z.string(), z.null()]).optional().default(null),
    public_holiday_calendar_id: z.union([z.number(), z.null()]).optional().default(null),
    public_holiday_calendar_name: z.union([z.string(), z.null()]).optional(),
    start_time: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_calendars_CalendarPOST = z.object({
    id: z.number().int().optional(),
    name: z.string(),
    stretches: z.array(calendars_api_calendars_CalendarStretch),
    timezone: z.string(),
});
const calendars_api_calendars_Calendar = z.object({
    id: z.number().int(),
    name: z.string(),
    stretches: z.array(calendars_api_calendars_CalendarStretch),
    timezone: z.string(),
});
const apiserverfoundation_management_configuration_ConfigurationSchemaGET = z.object({ config: z.unknown() });
const common_consumable_resources_ProjectLinkGET = z.object({
    id: z.number().int(),
    project_color: z.string(),
    project_id: z.number().int(),
    project_name: z.string(),
});
const common_consumable_resources_ConsumableResourceGET = z.object({
    abbreviation: z.string(),
    external_id: z.string(),
    id: z.number().int(),
    length: z.number(),
    name: z.string(),
    production_type_id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    project_links: z.array(common_consumable_resources_ProjectLinkGET),
});
const common_consumable_resources_ProjectLink = z.object({
    id: z.number().int().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int(),
    project_name: z.string().optional(),
});
const common_consumable_resources_ConsumableResourcePOST = z.object({
    abbreviation: z.string(),
    external_id: z.string(),
    id: z.number().int().optional(),
    length: z.number().optional(),
    name: z.string(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string().optional(),
    project_links: z.array(common_consumable_resources_ProjectLink).optional(),
});
const common_consumable_resources_ConsumableResource = z.object({
    abbreviation: z.string(),
    external_id: z.string(),
    id: z.number().int(),
    length: z.number().optional(),
    name: z.string(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string().optional(),
    project_links: z.array(common_consumable_resources_ProjectLink).optional(),
});
const calendars_api_countries_SubdivGET = z.object({
    code: z.string(),
    name: z.string(),
});
const calendars_api_countries_CountryGET = z.object({
    code: z.string(),
    name: z.string(),
    subdivs: z.array(calendars_api_countries_SubdivGET),
});
const apiserverfoundation_management_user_permissions_CurrentUserPermissionsSchemaGET = z.object({
    permissions: z.array(z.string()),
});
const apiserverfoundation_management_current_user_info_ComponentPermissionSchemaGET = z.object({
    component_key: z.string(),
    permission: z.enum(["READ_ONLY", "FULL_ACCESS"]),
});
const apiserverfoundation_management_current_user_info_UserRoleSchemaGET = z.object({
    component_permissions: z.array(apiserverfoundation_management_current_user_info_ComponentPermissionSchemaGET),
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_current_user_info_CurrentUserInfoSchemaGET = z.object({
    email: z.string().email(),
    full_name: z.string(),
    id: z.number().int(),
    is_admin: z.boolean(),
    is_external_login: z.boolean(),
    login: z.string(),
    roles: z.array(apiserverfoundation_management_current_user_info_UserRoleSchemaGET),
    state: z.enum(["INVALID", "NEW", "OK", "RESET_EMAIL", "RESET_PASSWORD"]),
});
const apiserverfoundation_management_current_user_info_ComponentPermissionSchema = z.object({
    component_key: z.string(),
    permission: z.enum(["READ_ONLY", "FULL_ACCESS"]).optional(),
});
const apiserverfoundation_management_current_user_info_UserRoleSchema = z.object({
    component_permissions: z
        .array(apiserverfoundation_management_current_user_info_ComponentPermissionSchema)
        .optional(),
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_current_user_info_CurrentUserInfoSchema = z.object({
    email: z.string().email().optional(),
    full_name: z.string(),
    id: z.number().int(),
    is_admin: z.boolean().optional(),
    is_external_login: z.boolean().optional(),
    login: z.string().optional(),
    roles: z.array(apiserverfoundation_management_current_user_info_UserRoleSchema).optional(),
    state: z.enum(["INVALID", "NEW", "OK", "RESET_EMAIL", "RESET_PASSWORD"]).optional(),
});
const apiserverfoundation_management_data_scope_DataScopeGET = z.object({
    key: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_data_scope_DataScopeSettingGET = z.object({
    all_scopes: z.array(apiserverfoundation_management_data_scope_DataScopeGET),
    current_scope: z.number().int(),
    default_scope: z.number().int(),
});
const apiserverfoundation_management_data_scope_DataScope = z
    .object({ key: z.number().int(), name: z.string() })
    .partial();
const apiserverfoundation_management_data_scope_DataScopeSetting = z.object({
    all_scopes: z.array(apiserverfoundation_management_data_scope_DataScope).optional(),
    current_scope: z.number().int(),
    default_scope: z.number().int(),
});
const apiserverfoundation_management_data_scope_DataScopeSchemaGET = z.object({
    id: z.number().int(),
    key: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_data_scope_DataScopeSchemaPOST = z.object({
    id: z.number().int().optional(),
    key: z.number().int().optional(),
    name: z.string(),
});
const apiserverfoundation_management_data_scope_DataScopeSchema = z.object({
    id: z.number().int(),
    key: z.number().int().optional(),
    name: z.string(),
});
const common_departments_DepartmentGET = z.object({
    external_id: z.string(),
    id: z.number().int(),
    name: z.string(),
});
const common_departments_DepartmentPOST = z.object({
    external_id: z.string(),
    id: z.number().int().optional(),
    name: z.string(),
});
const common_departments_Department = z.object({
    external_id: z.string(),
    id: z.number().int(),
    name: z.string(),
});
const filters_activity_templates_ActivityTemplatesFilterGET = z.object({
    external_id: z.string(),
    id: z.number().int(),
    name: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
});
const charts_flow_graphs_FlowGraphGET = z.object({
    description: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_template: z.boolean(),
    name: z.string(),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
});
const charts_flow_graphs_FlowEdge = z.object({
    edge_definition: z.object({}).partial().passthrough(),
    end_node_id: z.number().int(),
    id: z.number().int().optional(),
    name: z.union([z.string(), z.null()]).optional(),
    start_node_id: z.number().int(),
});
const charts_flow_nodes_FlowNode = z.object({
    description: z.string().optional(),
    id: z.number().int().optional(),
    is_template: z.boolean(),
    name: z.string(),
    node_definition: z.object({}).partial().passthrough(),
    operation_type_id: z.union([z.number(), z.null()]).optional(),
    operation_type_name: z.string().optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    shape_definition: z.object({}).partial().passthrough().optional(),
    shape_id: z.number().int(),
    shape_key: z.string().optional(),
});
const charts_flow_graphs_FlowGraphDetailPOST = z.object({
    description: z.union([z.string(), z.null()]).optional(),
    edges: z.array(charts_flow_graphs_FlowEdge).optional(),
    id: z.number().int().optional(),
    is_template: z.boolean(),
    name: z.string(),
    nodes: z.array(charts_flow_nodes_FlowNode).optional(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]).optional(),
});
const charts_flow_graphs_FlowGraphDetail = z.object({
    description: z.union([z.string(), z.null()]).optional(),
    edges: z.array(charts_flow_graphs_FlowEdge).optional(),
    id: z.number().int(),
    is_template: z.boolean(),
    name: z.string(),
    nodes: z.array(charts_flow_nodes_FlowNode).optional(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]).optional(),
    project_name: z.union([z.string(), z.null()]).optional(),
});
const charts_flow_graphs_FlowEdgeGET = z.object({
    edge_definition: z.object({}).partial().passthrough(),
    end_node_id: z.number().int(),
    id: z.number().int(),
    name: z.union([z.string(), z.null()]),
    start_node_id: z.number().int(),
});
const charts_flow_nodes_FlowNodeGET = z.object({
    description: z.string(),
    id: z.number().int(),
    is_template: z.boolean(),
    name: z.string(),
    node_definition: z.object({}).partial().passthrough(),
    operation_type_id: z.union([z.number(), z.null()]),
    operation_type_name: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
    shape_definition: z.object({}).partial().passthrough(),
    shape_id: z.number().int(),
    shape_key: z.string(),
});
const charts_flow_graphs_FlowGraphDetailGET = z.object({
    description: z.union([z.string(), z.null()]),
    edges: z.array(charts_flow_graphs_FlowEdgeGET),
    id: z.number().int(),
    is_template: z.boolean(),
    name: z.string(),
    nodes: z.array(charts_flow_nodes_FlowNodeGET),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
});
const charts_flow_nodes_FlowNodePOST = z.object({
    description: z.string().optional(),
    id: z.number().int().optional(),
    is_template: z.boolean(),
    name: z.string(),
    node_definition: z.object({}).partial().passthrough(),
    operation_type_id: z.union([z.number(), z.null()]).optional(),
    operation_type_name: z.string().optional(),
    production_type_key: z.nativeEnum(ProductionTypes),
    shape_definition: z.object({}).partial().passthrough().optional(),
    shape_id: z.number().int(),
    shape_key: z.string().optional(),
});
const charts_graph_shapes_GraphShapeGET = z.object({
    id: z.number().int(),
    shape_definition: z.object({}).partial().passthrough(),
    shape_key: z.string(),
    type_key: z.string(),
});
const charts_graph_shapes_GraphShapePOST = z.object({
    id: z.number().int().optional(),
    shape_definition: z.object({}).partial().passthrough(),
    shape_key: z.string(),
    type_key: z.string(),
});
const charts_graph_shapes_GraphShape = z.object({
    id: z.number().int(),
    shape_definition: z.object({}).partial().passthrough(),
    shape_key: z.string(),
    type_key: z.string(),
});
const data_import_activity_data_ActivityDataImportRequest = z.object({
    mips_file: z.instanceof(File),
    project_id: z.number().int(),
    safran_file: z.instanceof(File),
});
const data_import_activity_data_ActivityDataImportResponse = z
    .object({ message: z.string(), operation_id: z.number().int() })
    .partial();
const data_import_formats_ColumnGET = z.object({
    example_value: z.string(),
    header: z.string(),
    is_processed: z.boolean(),
    note: z.string(),
    required: z.boolean(),
});
const data_import_formats_ImportDataFormatGET = z.object({
    columns: z.array(data_import_formats_ColumnGET),
    note: z.string(),
});
const data_import_activity_data_ActivityDataImportFormatSchemaGET = z.object({
    mips: data_import_formats_ImportDataFormatGET,
    safran: data_import_formats_ImportDataFormatGET,
    cajac: data_import_formats_ImportDataFormatGET,
});
const data_import_animation_data_AnimationDataImportRequest = z.object({
    data_file: z.instanceof(File),
    project_id: z.number().int(),
});
const data_import_animation_data_AnimationDataImportResponse = z
    .object({ message: z.string(), operation_id: z.number().int() })
    .partial();
const data_import_future_project_FutureProjectDataImportRequest = z.object({
    cajac: z.instanceof(File).optional(),
    production_families: z.instanceof(File).optional(),
    project_id: z.number().int(),
    sequence: z.instanceof(File),
});
const data_import_future_project_FutureProjectDataImportResponse = z
    .object({ message: z.string(), operation_id: z.number().int() })
    .partial();
const data_import_future_project_FutureProjectDataImportFormatSchemaGET = z.object({
    cajac: data_import_formats_ImportDataFormatGET,
    production_families: data_import_formats_ImportDataFormatGET,
    sequence: data_import_formats_ImportDataFormatGET,
});
const data_import_vpl_planning_data_VPLPlanningDataImportRequest = z.object({
    project_id: z.number().int(),
    vpl1: z.instanceof(File),
    vpl2: z.instanceof(File),
});
const data_import_vpl_planning_data_VPLPlanningDataImportResponse = z
    .object({ message: z.string(), operation_id: z.number().int() })
    .partial();
const data_import_vpl_planning_data_VPLPlanningDataImportFormatSchemaGET = z.object({
    vpl1: data_import_formats_ImportDataFormatGET,
    vpl2: data_import_formats_ImportDataFormatGET,
});
const label_types = z.union([z.array(z.string()), z.null()]).optional();
const common_labels_LabelGET = z.object({
    description: z.string(),
    id: z.number().int(),
    label_types: z.union([z.array(z.string()), z.null()]),
    locked: z.boolean(),
    name: z.string(),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
});
const common_labels_LabelPOST = z.object({
    description: z.string(),
    id: z.number().int().optional(),
    label_types: z.union([z.array(z.string()), z.null()]),
    locked: z.boolean().optional(),
    name: z.string(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]).optional(),
});
const common_labels_Label = z.object({
    description: z.string(),
    id: z.number().int(),
    label_types: z.union([z.array(z.string()), z.null()]),
    locked: z.boolean().optional(),
    name: z.string(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]).optional(),
});
const oplog_web_api_operation_logs_MetadataGET = z.object({
    messages: z.array(z.object({}).partial().passthrough()),
    retry_with_force_is_possible: z.boolean(),
});
const oplog_web_api_operation_logs_GlobalOperationLogGET = z.object({
    activity_key: z.string(),
    end_time: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int(),
    message: z.string(),
    metadata: oplog_web_api_operation_logs_MetadataGET,
    start_time: z.unknown().transform(transformUnknownToDateTime),
    status_code: z.number().int(),
    username: z.string(),
});
const common_operation_logs_MetadataMiniGET = z.object({
    error_count: z.number().int(),
    warning_count: z.number().int(),
});
const common_operation_logs_GlobalOperationLogMiniGET = z.object({
    activity_key: z.string(),
    end_time: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int(),
    message: z.string(),
    metadata: common_operation_logs_MetadataMiniGET,
    start_time: z.unknown().transform(transformUnknownToDateTime),
    status_code: z.number().int(),
    username: z.string(),
});
const oplog_web_api_templates_TemplateGET = z.object({
    code: z.number().int(),
    template: z.string(),
});
const common_operation_logs_LogExportTofileGET = z.object({}).partial();
const common_operation_logs_LogEntryRawGET = z.object({
    code: z.number().int(),
});
const calendars_api_materialized_calendar_SegmentGET = z.object({
    end: z.unknown().transform(transformUnknownToDateTime),
    note: z.union([z.string(), z.null()]),
    shift_id: z.number().int(),
    start: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_materialized_calendar_MaterializedCalendarStretchGET = z.object({
    calendar_pattern_id: z.number().int(),
    calendar_pattern_name: z.string(),
    id: z.number().int(),
    note: z.union([z.string(), z.null()]),
    public_holiday_calendar_id: z.union([z.number(), z.null()]),
    public_holiday_calendar_name: z.union([z.string(), z.null()]),
    segments: z.array(calendars_api_materialized_calendar_SegmentGET),
    start_time: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_materialized_calendar_MaterializedCalendarGET = z.object({
    id: z.number().int(),
    name: z.string(),
    stretches: z.array(calendars_api_materialized_calendar_MaterializedCalendarStretchGET),
    timezone: z.string(),
});
const common_operation_types_OperationTypeGET = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_operation_types_OperationType = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_processing_characteristics_ProcessingCharacteristicGET = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_processing_characteristics_ProcessingCharacteristicPOST = z.object({
    id: z.number().int().optional(),
    key: z.string(),
    name: z.string(),
});
const common_processing_characteristics_ProcessingCharacteristic = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_production_lines_ProductionLineStationGET = z.object({
    capacity: z.number().int(),
    id: z.number().int(),
    name: z.string(),
});
const common_production_lines_ProductionLineGET = z.object({
    abbreviation: z.string(),
    color: z.string(),
    id: z.number().int(),
    name: z.string(),
    stations: z.array(common_production_lines_ProductionLineStationGET),
});
const common_production_lines_ProductionLineStation = z.object({
    capacity: z.number().int().optional(),
    id: z.number().int(),
    name: z.string().optional(),
});
const common_production_lines_ProductionLinePOST = z.object({
    abbreviation: z.string(),
    color: z.string(),
    id: z.number().int().optional(),
    name: z.string(),
    stations: z.array(common_production_lines_ProductionLineStation).optional(),
});
const common_production_lines_ProductionLine = z.object({
    abbreviation: z.string(),
    color: z.string(),
    id: z.number().int(),
    name: z.string(),
    stations: z.array(common_production_lines_ProductionLineStation).optional(),
});
const schedule_id = z.union([z.number(), z.null()]).optional().default(null);
const charts_production_plan_batch_ProductionPlanBatchSchemaGET = z.object({
    activities: z.array(common_activities_ActivityGET),
    consumable_resources: z.array(common_consumable_resources_ConsumableResourceGET),
    total_length: z.number(),
});
const common_production_types_ProductionTypeGET = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_production_types_operation_types_ProductionTypesOperationTypesGET = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
    operation_types: z.array(common_operation_types_OperationTypeGET),
});
const charts_project_timelines_ChartItemGET = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    activity_category_name: z.union([z.string(), z.null()]),
    activity_template_external_id: z.string(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    batch_id: z.number().int(),
    batch_name: z.string(),
    category_id: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    is_artificial: z.boolean(),
    is_group: z.boolean(),
    is_highlighted: z.boolean(),
    is_visible: z.boolean(),
    name: z.union([z.string(), z.null()]),
    on_critical_path: z.boolean(),
    operation_type_id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    progress: z.number(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    station_id: z.union([z.number(), z.null()]),
    station_name: z.union([z.string(), z.null()]),
    time_delta: z.union([z.number(), z.null()]),
});
const charts_project_timelines_CategoryGET = z.object({
    external_id: z.string(),
    id: z.string(),
    is_highlighted: z.boolean(),
    items: z.array(charts_project_timelines_ChartItemGET),
    name: z.string(),
    parent_category_id: z.union([z.string(), z.null()]),
    production_type_key: z.nativeEnum(ProductionTypes),
});
const charts_project_timelines_ChartFlowLinesGET = z.object({
    end_activity_id: z.number().int(),
    is_highlighted: z.boolean(),
    is_visible: z.boolean(),
    source_pos: z.number(),
    start_activity_id: z.number().int(),
    target_pos: z.number(),
    timedelta: z.number().int(),
});
const charts_project_timelines_MilestoneMiniGET = z.object({
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    name: z.string(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
});
const charts_project_timelines_ProjectTimelineGET = z.object({
    categories: z.array(charts_project_timelines_CategoryGET),
    flow_lines: z.array(charts_project_timelines_ChartFlowLinesGET),
    milestones: z.array(charts_project_timelines_MilestoneMiniGET),
    visible_category_ids: z.array(z.string()),
});
const common_projects_BatchGET = z.object({
    due_date: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int(),
    name: z.string(),
    release_date: z.unknown().transform(transformUnknownToDateTime),
});
const common_projects_ProjectGET = z.object({
    abbreviation: z.string(),
    batches: z.array(common_projects_BatchGET),
    calendar_id: z.union([z.number(), z.null()]),
    color: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime),
    flow_graph_id: z.union([z.number(), z.null()]),
    id: z.number().int(),
    is_archived: z.boolean(),
    name: z.string(),
    release_date: z.unknown().transform(transformUnknownToDateTime),
});
const common_projects_Batch = z.object({
    due_date: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int().optional(),
    name: z.string().optional(),
    release_date: z.unknown().transform(transformUnknownToDateTime),
});
const common_projects_ProjectPOST = z.object({
    abbreviation: z.string(),
    batches: z.array(common_projects_Batch).optional(),
    calendar_id: z.union([z.number(), z.null()]),
    color: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime),
    flow_graph_id: z.union([z.number(), z.null()]).optional(),
    id: z.number().int().optional(),
    is_archived: z.boolean(),
    name: z.string(),
    release_date: z.unknown().transform(transformUnknownToDateTime),
});
const common_projects_Project = z.object({
    abbreviation: z.string(),
    batches: z.array(common_projects_Batch).optional(),
    calendar_id: z.union([z.number(), z.null()]),
    color: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime),
    flow_graph_id: z.union([z.number(), z.null()]).optional(),
    id: z.number().int(),
    is_archived: z.boolean(),
    name: z.string(),
    release_date: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_public_holiday_calendars_PublicHolidayGET = z.object({
    date: z.unknown().transform(transformUnknownToDateTime),
    name: z.string(),
});
const calendars_api_public_holiday_calendars_PublicHolidayCalendarGET = z.object({
    country: z.string(),
    id: z.number().int(),
    language: z.union([z.string(), z.null()]),
    name: z.string(),
    public_holidays: z.array(calendars_api_public_holiday_calendars_PublicHolidayGET),
    subdiv: z.union([z.string(), z.null()]),
});
const calendars_api_public_holiday_calendars_PublicHoliday = z
    .object({
        date: z.unknown().transform(transformUnknownToDateTime),
        name: z.string(),
    })
    .partial();
const calendars_api_public_holiday_calendars_PublicHolidayCalendarPOST = z.object({
    country: z.string(),
    id: z.number().int().optional(),
    language: z.union([z.string(), z.null()]).optional().default(null),
    name: z.string(),
    public_holidays: z.array(calendars_api_public_holiday_calendars_PublicHoliday).optional(),
    subdiv: z.union([z.string(), z.null()]).optional().default(null),
});
const calendars_api_public_holiday_calendars_PublicHolidayCalendar = z.object({
    country: z.string(),
    id: z.number().int(),
    language: z.union([z.string(), z.null()]).optional().default(null),
    name: z.string(),
    public_holidays: z.array(calendars_api_public_holiday_calendars_PublicHoliday).optional(),
    subdiv: z.union([z.string(), z.null()]).optional().default(null),
});
const common_reference_values_ReferenceValueGET = z.object({
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_resend_activation_email_ResendActivationEmailSchema = z.object({}).partial();
const apiserverfoundation_management_reset_password_ResetPasswordSchema = z.object({ email: z.string().email() });
const apiserverfoundation_management_reset_password_ResetPasswordResponseSchema = z.object({
    message: z.string(),
    status: z.enum(["FAILURE", "SUCCESS"]).optional(),
});
const apiserverfoundation_management_roles_EndpointInfoSchemaGET = z.object({
    endpoint: z.string(),
    method: z.enum(["GET", "POST", "PUT", "DELETE"]),
});
const apiserverfoundation_management_roles_ComponentPermissionSchemaGET = z.object({
    api_endpoints: z.array(apiserverfoundation_management_roles_EndpointInfoSchemaGET),
    component_key: z.string(),
    permission: z.enum(["READ_ONLY", "FULL_ACCESS"]),
});
const apiserverfoundation_management_roles_RoleSchemaGET = z.object({
    component_permissions: z.array(apiserverfoundation_management_roles_ComponentPermissionSchemaGET),
    description: z.string(),
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_roles_EndpointInfoSchema = z
    .object({
        endpoint: z.string(),
        method: z.enum(["GET", "POST", "PUT", "DELETE"]),
    })
    .partial();
const apiserverfoundation_management_roles_ComponentPermissionSchema = z.object({
    api_endpoints: z.array(apiserverfoundation_management_roles_EndpointInfoSchema).optional(),
    component_key: z.string(),
    permission: z.enum(["READ_ONLY", "FULL_ACCESS"]).optional(),
});
const apiserverfoundation_management_roles_RoleSchemaPOST = z.object({
    component_permissions: z.array(apiserverfoundation_management_roles_ComponentPermissionSchema).optional(),
    description: z.string().optional().default(""),
    id: z.number().int().optional(),
    name: z.string(),
});
const apiserverfoundation_management_roles_RoleSchema = z.object({
    component_permissions: z.array(apiserverfoundation_management_roles_ComponentPermissionSchema).optional(),
    description: z.string().optional().default(""),
    id: z.number().int(),
    name: z.string(),
});
const calculations_schedule_activities_AssemblyParamsGET = z.object({
    project_ids: z.array(z.number().int()),
});
const calculations_schedule_activities_VPLParamsGET = z.object({
    maxTime: z.number().int(),
    mode: z.string(),
    speedPreference: z.number().int(),
});
const calculations_schedule_activities_ScheduleActivitiesRequestGET = z.object({
    assembly_params: z.union([calculations_schedule_activities_AssemblyParamsGET, z.null()]),
    note: z.union([z.string(), z.null()]),
    schedule_id: z.number().int(),
    vpl_params: z.union([calculations_schedule_activities_VPLParamsGET, z.null()]),
});
const calculations_schedule_activities_ScheduleActivitiesProcessStatusGET = z.object({
    args: calculations_schedule_activities_ScheduleActivitiesRequestGET,
    calculations: z.array(z.string()),
    created: z.unknown().transform(transformUnknownToDateTime),
    error: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_finished: z.boolean(),
    is_owner: z.boolean(),
    logs: z.array(z.string()),
    name: z.string(),
    progress: z.number(),
    return_code: z.number().int(),
    updated: z.unknown().transform(transformUnknownToDateTime),
    user_name: z.string(),
    uuid: z.string(),
});
const calculations_schedule_activities_AssemblyParams = z.object({
    project_ids: z.array(z.number().int()),
});
const calculations_schedule_activities_VPLParams = z
    .object({
        maxTime: z.number().int().default(300),
        mode: z.string().default("JIT"),
        speedPreference: z.number().int().default(1),
    })
    .partial();
const calculations_schedule_activities_ScheduleActivitiesRequest = z.object({
    assembly_params: z.union([calculations_schedule_activities_AssemblyParams, z.null()]),
    note: z.union([z.string(), z.null()]),
    schedule_id: z.number().int(),
    vpl_params: z.union([calculations_schedule_activities_VPLParams, z.null()]),
});
const calculations_schedule_activities_ScheduleActivitiesProcessStatus = z.object({
    args: calculations_schedule_activities_ScheduleActivitiesRequest.optional(),
    calculations: z.array(z.string()).optional(),
    created: z.unknown().transform(transformUnknownToDateTime),
    error: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_finished: z.boolean(),
    is_owner: z.boolean(),
    logs: z.array(z.string()).optional(),
    name: z.string(),
    progress: z.number(),
    return_code: z.number().int(),
    updated: z.unknown().transform(transformUnknownToDateTime).optional(),
    user_name: z.string(),
    uuid: z.string(),
});
const calculations_schedule_assembly_activities_AssemblySchedulingRequest = z
    .object({
        batch_id: z.union([z.number(), z.null()]).default(null),
        project_id: z.union([z.number(), z.null()]).default(null),
    })
    .partial();
const calculations_schedule_assembly_activities_AssemblySchedulingResult = z.object({}).partial();
const project_id__2 = z.union([z.string(), z.null()]).optional().default(null);
const charts_schedule_chart_categories_StationActivityGET = z.object({
    color: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    err_flags: z.number().int(),
    id: z.number().int(),
    name: z.string(),
    num_tranches: z.number().int(),
    progress: z.number(),
    pt_key: z.nativeEnum(ProductionTypes),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    time_delta: z.union([z.number(), z.null()]),
    tranche_index: z.number().int(),
    visible: z.boolean(),
});
const charts_schedule_chart_categories_CategoryGET = z.object({
    id: z.number().int(),
    items: z.array(charts_schedule_chart_categories_StationActivityGET),
    name: z.string(),
});
const charts_schedule_chart_categories_ScheduleChartSchemaGET = z.object({
    categories: z.array(charts_schedule_chart_categories_CategoryGET),
});
const calculations_schedule_production_lines_ProcessRequestGET = z.object({
    maxTime: z.number().int(),
    speedPreference: z.number().int(),
});
const calculations_schedule_production_lines_ProcessStatusGET = z.object({
    args: calculations_schedule_production_lines_ProcessRequestGET,
    calculations: z.array(z.string()),
    created: z.unknown().transform(transformUnknownToDateTime),
    error: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_finished: z.boolean(),
    is_owner: z.boolean(),
    logs: z.array(z.string()),
    name: z.string(),
    progress: z.number(),
    return_code: z.number().int(),
    updated: z.unknown().transform(transformUnknownToDateTime),
    user_name: z.string(),
    uuid: z.string(),
});
const calculations_schedule_production_lines_ProcessRequest = z
    .object({
        maxTime: z.number().int().default(300),
        speedPreference: z.number().int().default(1),
    })
    .partial();
const calculations_schedule_production_lines_ProcessStatus = z
    .object({
        args: calculations_schedule_production_lines_ProcessRequest,
        calculations: z.array(z.string()),
        created: z.unknown().transform(transformUnknownToDateTime),
        error: z.union([z.string(), z.null()]),
        id: z.number().int(),
        is_finished: z.boolean(),
        is_owner: z.boolean(),
        logs: z.array(z.string()),
        name: z.string(),
        progress: z.number(),
        return_code: z.number().int(),
        updated: z.unknown().transform(transformUnknownToDateTime),
        user_name: z.string(),
        uuid: z.string(),
    })
    .partial();
const common_schedules_ScheduleParamsGET = z.object({
    assembly_params: z.union([calculations_schedule_activities_AssemblyParamsGET, z.null()]),
    vpl_params: z.union([calculations_schedule_activities_VPLParamsGET, z.null()]),
});
const common_schedules_ScheduleProjectsGET = z.object({
    color: z.union([z.string(), z.null()]),
    id: z.union([z.number(), z.null()]),
    name: z.union([z.string(), z.null()]),
    scheduled_at: z.unknown().transform(transformUnknownToDateTime).nullable(),
    updated_at: z.unknown().transform(transformUnknownToDateTime).nullable(),
});
const common_schedules_ScheduleGET = z.object({
    created_at: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int(),
    name: z.string(),
    note: z.union([z.string(), z.null()]),
    params: z.union([common_schedules_ScheduleParamsGET, z.null()]),
    projects: z.array(common_schedules_ScheduleProjectsGET),
});
const common_schedules_ScheduleParams = z.object({
    assembly_params: z.union([calculations_schedule_activities_AssemblyParams, z.null()]),
    vpl_params: z.union([calculations_schedule_activities_VPLParams, z.null()]),
});
const common_schedules_ScheduleProjects = z
    .object({
        color: z.union([z.string(), z.null()]),
        id: z.union([z.number(), z.null()]),
        name: z.union([z.string(), z.null()]),
        scheduled_at: z.unknown().transform(transformUnknownToDateTime).nullable(),
        updated_at: z.unknown().transform(transformUnknownToDateTime).nullable(),
    })
    .partial();
const common_schedules_SchedulePOST = z.object({
    created_at: z.unknown().transform(transformUnknownToDateTime).optional(),
    id: z.number().int().optional(),
    name: z.string(),
    note: z.union([z.string(), z.null()]),
    params: z.union([common_schedules_ScheduleParams, z.null()]).optional(),
    projects: z.array(common_schedules_ScheduleProjects).optional(),
});
const common_schedules_Schedule = z.object({
    created_at: z.unknown().transform(transformUnknownToDateTime).optional(),
    id: z.number().int(),
    name: z.string(),
    note: z.union([z.string(), z.null()]),
    params: z.union([common_schedules_ScheduleParams, z.null()]).optional(),
    projects: z.array(common_schedules_ScheduleProjects).optional(),
});
const common_copy_schedule_CopySchedulePOST = z.object({
    created_at: z.unknown().transform(transformUnknownToDateTime).optional(),
    id: z.number().int().optional(),
    name: z.string(),
    note: z.union([z.string(), z.null()]),
    params: z.union([common_schedules_ScheduleParams, z.null()]).optional(),
    projects: z.array(common_schedules_ScheduleProjects).optional(),
    source_schedule_id: z.number().int(),
});
const common_activities_ScheduleValuesGET = z.object({
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    station_abbreviation: z.union([z.string(), z.null()]),
    station_id: z.union([z.number(), z.null()]),
    station_name: z.union([z.string(), z.null()]),
    time_delta: z.union([z.number(), z.null()]),
});
const common_activities_ScheduledActivityGET = z.object({
    activity_category_id: z.union([z.number(), z.null()]),
    activity_category_name: z.union([z.string(), z.null()]),
    activity_template_external_id: z.string(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string(),
    batch_id: z.number().int(),
    batch_name: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    name: z.string(),
    operation_type_id: z.number().int(),
    production_type_id: z.number().int(),
    production_type_key: z.nativeEnum(ProductionTypes),
    production_type_name: z.string(),
    progress: z.number(),
    project_color: z.string(),
    project_id: z.number().int(),
    project_name: z.string(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    schedule_values: z.union([common_activities_ScheduleValuesGET, z.null()]),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    station_abbreviation: z.union([z.string(), z.null()]),
    station_id: z.union([z.number(), z.null()]),
    station_name: z.union([z.string(), z.null()]),
    time_delta: z.union([z.number(), z.null()]),
});
const common_activities_ScheduleValues = z
    .object({
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        station_abbreviation: z.union([z.string(), z.null()]),
        station_id: z.union([z.number(), z.null()]),
        station_name: z.union([z.string(), z.null()]),
        time_delta: z.union([z.number(), z.null()]),
    })
    .partial();
const common_activities_ScheduledActivity = z.object({
    activity_category_id: z.union([z.number(), z.null()]).optional(),
    activity_category_name: z.union([z.string(), z.null()]).optional(),
    activity_template_external_id: z.string().optional(),
    activity_template_id: z.number().int(),
    activity_template_name: z.string().optional(),
    batch_id: z.number().int().optional(),
    batch_name: z.string().optional(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    id: z.number().int(),
    name: z.string(),
    operation_type_id: z.number().int().optional(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    progress: z.number().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int().optional(),
    project_name: z.string().optional(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    schedule_values: z.union([common_activities_ScheduleValues, z.null()]).optional(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullish(),
    station_abbreviation: z.union([z.string(), z.null()]).optional(),
    station_id: z.union([z.number(), z.null()]).optional(),
    station_name: z.union([z.string(), z.null()]).optional(),
    time_delta: z.union([z.number(), z.null()]).optional(),
});
const common_activity_relationships_ScheduledActivityRelationshipItemGET = z.object({
    activity_external_id: z.string(),
    activity_id: z.number().int(),
    activity_name: z.string(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    offset: z.number(),
    production_type_key: z.nativeEnum(ProductionTypes),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    schedule_values: common_activities_ScheduleValuesGET,
    source_pos: z.number(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    target_pos: z.number(),
    time_delta: z.number(),
});
const common_activity_relationships_ScheduledActivityRelationshipsGET = z.object({
    predecessors: z.array(common_activity_relationships_ScheduledActivityRelationshipItemGET),
    successors: z.array(common_activity_relationships_ScheduledActivityRelationshipItemGET),
});
const common_batch_templates_with_activity_ScheduledBatchActivityTemplateWithActivityGET = z.object({
    activity: z.union([common_activities_ScheduledActivityGET, z.null()]),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    img_name: z.union([z.string(), z.null()]),
    labels: z.array(common_labels_LabelMiniGET),
    man_hours: z.number(),
    name: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes),
});
const charts_project_timeline_schedules_OverlayChartItemGET = z.object({
    activity_id: z.number().int(),
    due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    time_delta: z.union([z.number(), z.null()]),
});
const charts_project_timeline_schedules_OverlayFlowLineGET = z.object({
    end_activity_id: z.number().int(),
    start_activity_id: z.number().int(),
    timedelta: z.number().int(),
});
const charts_project_timeline_schedules_ProjectTimelineScheduleGET = z.object({
    chart_items: z.array(charts_project_timeline_schedules_OverlayChartItemGET),
    flow_lines: z.array(charts_project_timeline_schedules_OverlayFlowLineGET),
});
const common_site_locations_SiteLocationGET = z.object({
    description: z.string(),
    external_id: z.string(),
    id: z.number().int(),
    is_external: z.boolean(),
    name: z.string(),
});
const common_site_locations_SiteLocationPOST = z.object({
    description: z.string().optional(),
    external_id: z.string(),
    id: z.number().int().optional(),
    is_external: z.boolean(),
    name: z.string(),
});
const common_site_locations_SiteLocation = z.object({
    description: z.string().optional(),
    external_id: z.string(),
    id: z.number().int(),
    is_external: z.boolean(),
    name: z.string(),
});
const common_station_detail_StationCapability = z.object({
    id: z.number().int().optional(),
    operation_type_id: z.number().int(),
    operation_type_key: z.string().optional(),
    operation_type_name: z.string().optional(),
    processing_characteristic_id: z.number().int(),
    processing_characteristic_key: z.string().optional(),
    processing_characteristic_name: z.string().optional(),
});
const common_station_detail_StationDetailPOST = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capabilities: z.array(common_station_detail_StationCapability).optional(),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int().optional(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string().optional(),
    project_id: z.union([z.number(), z.null()]),
});
const common_station_detail_StationDetail = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capabilities: z.array(common_station_detail_StationCapability).optional(),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string().optional(),
    project_id: z.union([z.number(), z.null()]),
});
const common_station_detail_StationCapabilityGET = z.object({
    id: z.number().int(),
    operation_type_id: z.number().int(),
    operation_type_key: z.string(),
    operation_type_name: z.string(),
    processing_characteristic_id: z.number().int(),
    processing_characteristic_key: z.string(),
    processing_characteristic_name: z.string(),
});
const common_station_detail_StationDetailGET = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capabilities: z.array(common_station_detail_StationCapabilityGET),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string(),
    project_id: z.union([z.number(), z.null()]),
});
const common_station_yard_area_reservations_StationYardAreaReservationGET = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
    yard_area_id: z.number().int(),
    yard_area_name: z.string(),
});
const common_stations_StationGET = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string(),
    project_id: z.union([z.number(), z.null()]),
});
const common_stations_StationPOST = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int().optional(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string().optional(),
    project_id: z.union([z.number(), z.null()]),
});
const common_stations_Station = z.object({
    abbreviation: z.string(),
    calendar_id: z.union([z.number(), z.null()]),
    capacity: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    is_permanent: z.boolean(),
    name: z.string(),
    production_line_name: z.string().optional(),
    project_id: z.union([z.number(), z.null()]),
});
const common_update_activities_Batch = z.object({
    add_activity_template_ids: z
        .union([z.array(z.number().int()), z.null()])
        .optional()
        .default(null),
    batch_id: z.number().int(),
    remove_activity_template_ids: z
        .union([z.array(z.number().int()), z.null()])
        .optional()
        .default(null),
    synchronize: z.boolean().optional().default(false),
});
const common_update_activities_UpdateActivityRequest = z.object({
    batches: z.array(common_update_activities_Batch),
    production_type_key: z.nativeEnum(ProductionTypes),
    project_id: z.number().int(),
});
const common_update_activities_ResponseActivityMapping = z
    .object({
        activity_template_id: z.number().int(),
        batch_id: z.number().int(),
    })
    .partial();
const common_update_activities_UpdateActivityResponse = z
    .object({
        added_activities: z.array(common_update_activities_ResponseActivityMapping),
        production_type_key: z.nativeEnum(ProductionTypes),
        project_id: z.number().int(),
        removed_activities: z.array(common_update_activities_ResponseActivityMapping),
    })
    .partial();
const common_yard_areas_EntryPoint = z.object({
    abbreviation: z.string(),
    deleted: z.boolean(),
    id: z.number().int(),
    lat: z.number(),
    lng: z.number(),
    name: z.string(),
});
const common_yard_areas_GeoPoint = z.object({
    lat: z.number(),
    lng: z.number(),
});
const common_yard_areas_AreaStationReservation = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int().optional(),
    is_permanent: z.boolean().optional(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]).optional(),
    station_id: z.number().int(),
    station_name: z.string().optional(),
});
const common_yard_areas_AreaStationEntryPoint = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int().optional(),
    in_entry_point_id: z.number().int(),
    in_entry_point_name: z.string().optional(),
    out_entry_point_id: z.union([z.number(), z.null()]),
    out_entry_point_name: z.union([z.string(), z.null()]).optional(),
    station_id: z.number().int(),
    station_name: z.string().optional(),
});
const common_yard_areas_AreaStation = z.object({
    entrypoint_link_id: z.union([z.number(), z.null()]),
    id: z.number().int().optional(),
    in_entry_point_id: z.number().int(),
    in_entry_point_name: z.string().optional(),
    is_permanent: z.boolean().optional(),
    out_entry_point_id: z.union([z.number(), z.null()]),
    out_entry_point_name: z.union([z.string(), z.null()]).optional(),
    station_id: z.number().int(),
    station_name: z.string().optional(),
});
const common_yard_areas_YardAreaPOST = z.object({
    abbreviation: z.string(),
    area_definition: z.object({}).partial().passthrough(),
    area_type: z.string(),
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    department_id: z.union([z.number(), z.null()]),
    entry_points: z.array(common_yard_areas_EntryPoint),
    geodata: z.array(common_yard_areas_GeoPoint),
    id: z.number().int().optional(),
    name: z.string(),
    parent_area_id: z.union([z.number(), z.null()]),
    reservations: z.array(common_yard_areas_AreaStationReservation),
    station_entry_points: z.array(common_yard_areas_AreaStationEntryPoint),
    stations: z.array(common_yard_areas_AreaStation),
});
const common_yard_areas_YardArea = z.object({
    abbreviation: z.string(),
    area_definition: z.object({}).partial().passthrough(),
    area_type: z.string(),
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    department_id: z.union([z.number(), z.null()]),
    entry_points: z.array(common_yard_areas_EntryPoint),
    geodata: z.array(common_yard_areas_GeoPoint),
    id: z.number().int(),
    name: z.string(),
    parent_area_id: z.union([z.number(), z.null()]),
    reservations: z.array(common_yard_areas_AreaStationReservation),
    station_entry_points: z.array(common_yard_areas_AreaStationEntryPoint),
    stations: z.array(common_yard_areas_AreaStation),
});
const common_update_yard_areas_UpdateYardAreaRequest = z.object({
    added_areas: z.array(common_yard_areas_YardAreaPOST),
    removed_area_ids: z.array(z.number().int()),
    time_interval_end: z.unknown().transform(transformUnknownToDateTime),
    time_interval_start: z.unknown().transform(transformUnknownToDateTime),
    updated_areas: z.array(common_yard_areas_YardArea),
});
const common_update_yard_areas_UpdateYardAreaResponse = z.object({ status: z.string() }).partial();
const apiserverfoundation_management_user_settings_UserSettingsSchemaGET = z.object({}).partial();
const apiserverfoundation_management_user_settings_UserSettingsRequestPutSchema = z
    .object({
        settings: z.object({}).partial().passthrough(),
        user_login: z.string(),
    })
    .partial();
const apiserverfoundation_management_user_settings_UserSettingsSchema = z.object({}).partial();
const apiserverfoundation_management_users_DataScopeSchemaGET = z.object({
    id: z.number().int(),
    key: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_users_SimpleRoleSchemaGET = z.object({
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_users_UsersSchemaGET = z.object({
    data_scopes: z.array(apiserverfoundation_management_users_DataScopeSchemaGET),
    email: z.string().email(),
    full_name: z.string(),
    id: z.number().int(),
    is_active: z.boolean(),
    is_admin: z.boolean(),
    last_login: z.unknown().transform(transformUnknownToDateTime).nullable(),
    login: z.string(),
    roles: z.array(apiserverfoundation_management_users_SimpleRoleSchemaGET),
    state: z.enum(["INVALID", "NEW", "OK", "RESET_EMAIL", "RESET_PASSWORD"]),
});
const apiserverfoundation_management_users_DataScopeSchema = z.object({
    id: z.number().int(),
    key: z.number().int().optional(),
    name: z.string().optional(),
});
const apiserverfoundation_management_users_SimpleRoleSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});
const apiserverfoundation_management_users_UsersSchemaPOST = z.object({
    data_scopes: z.array(apiserverfoundation_management_users_DataScopeSchema).optional().default([]),
    email: z.string().email(),
    full_name: z.string(),
    id: z.number().int().optional(),
    is_active: z.boolean(),
    is_admin: z.boolean(),
    last_login: z.unknown().transform(transformUnknownToDateTime).nullish(),
    login: z.string(),
    roles: z.array(apiserverfoundation_management_users_SimpleRoleSchema),
    state: z.enum(["INVALID", "NEW", "OK", "RESET_EMAIL", "RESET_PASSWORD"]).optional(),
});
const apiserverfoundation_management_users_UsersSchema = z.object({
    data_scopes: z.array(apiserverfoundation_management_users_DataScopeSchema).optional().default([]),
    email: z.string().email(),
    full_name: z.string(),
    id: z.number().int(),
    is_active: z.boolean(),
    is_admin: z.boolean(),
    last_login: z.unknown().transform(transformUnknownToDateTime).nullish(),
    login: z.string(),
    roles: z.array(apiserverfoundation_management_users_SimpleRoleSchema),
    state: z.enum(["INVALID", "NEW", "OK", "RESET_EMAIL", "RESET_PASSWORD"]).optional(),
});
const apiserverfoundation_management_app_version_VersionSchemaGET = z.object({
    server_revision: z.string(),
    server_version: z.string(),
});
const calculations_visualize_geometry_VisualizeGeometryRequestGET = z.object({
    project_id: z.number().int(),
    zip_file: z.instanceof(File),
});
const calculations_visualize_geometry_VisualizeGeometryRequestProcessStatusGET = z.object({
    args: calculations_visualize_geometry_VisualizeGeometryRequestGET,
    calculations: z.array(z.string()),
    created: z.unknown().transform(transformUnknownToDateTime),
    error: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_finished: z.boolean(),
    is_owner: z.boolean(),
    logs: z.array(z.string()),
    name: z.string(),
    progress: z.number(),
    return_code: z.number().int(),
    updated: z.unknown().transform(transformUnknownToDateTime),
    user_name: z.string(),
    uuid: z.string(),
});
const calculations_visualize_geometry_VisualizeGeometryRequest = z.object({
    project_id: z.number().int(),
    zip_file: z.instanceof(File),
});
const calculations_visualize_geometry_VisualizeGeometryRequestProcessStatus = z.object({
    args: calculations_visualize_geometry_VisualizeGeometryRequest.optional(),
    calculations: z.array(z.string()).optional(),
    created: z.unknown().transform(transformUnknownToDateTime),
    error: z.union([z.string(), z.null()]),
    id: z.number().int(),
    is_finished: z.boolean(),
    is_owner: z.boolean(),
    logs: z.array(z.string()).optional(),
    name: z.string(),
    progress: z.number(),
    return_code: z.number().int(),
    updated: z.unknown().transform(transformUnknownToDateTime).optional(),
    user_name: z.string(),
    uuid: z.string(),
});
const calculations_visualize_geometry_ServeVisualizationFileSchemaGET = z.object({}).partial();
const common_yard_areas_EntryPointGET = z.object({
    abbreviation: z.string(),
    deleted: z.boolean(),
    id: z.number().int(),
    lat: z.number(),
    lng: z.number(),
    name: z.string(),
});
const common_yard_areas_GeoPointGET = z.object({
    lat: z.number(),
    lng: z.number(),
});
const common_yard_areas_AreaStationReservationGET = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    is_permanent: z.boolean(),
    project_color: z.union([z.string(), z.null()]),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]),
    station_id: z.number().int(),
    station_name: z.string(),
});
const common_yard_areas_AreaStationEntryPointGET = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int(),
    in_entry_point_id: z.number().int(),
    in_entry_point_name: z.string(),
    out_entry_point_id: z.union([z.number(), z.null()]),
    out_entry_point_name: z.union([z.string(), z.null()]),
    station_id: z.number().int(),
    station_name: z.string(),
});
const common_yard_areas_AreaStationGET = z.object({
    entrypoint_link_id: z.union([z.number(), z.null()]),
    id: z.number().int(),
    in_entry_point_id: z.number().int(),
    in_entry_point_name: z.string(),
    is_permanent: z.boolean(),
    out_entry_point_id: z.union([z.number(), z.null()]),
    out_entry_point_name: z.union([z.string(), z.null()]),
    station_id: z.number().int(),
    station_name: z.string(),
});
const common_yard_areas_YardAreaGET = z.object({
    abbreviation: z.string(),
    area_definition: z.object({}).partial().passthrough(),
    area_type: z.string(),
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    department_id: z.union([z.number(), z.null()]),
    entry_points: z.array(common_yard_areas_EntryPointGET),
    geodata: z.array(common_yard_areas_GeoPointGET),
    id: z.number().int(),
    name: z.string(),
    parent_area_id: z.union([z.number(), z.null()]),
    reservations: z.array(common_yard_areas_AreaStationReservationGET),
    station_entry_points: z.array(common_yard_areas_AreaStationEntryPointGET),
    stations: z.array(common_yard_areas_AreaStationGET),
});
const common_yard_areas_YardAreaForPut = z.object({
    abbreviation: z.string(),
    area_definition: z.object({}).partial().passthrough(),
    area_type: z.string(),
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    department_id: z.union([z.number(), z.null()]),
    entry_points: z.array(common_yard_areas_EntryPoint),
    geodata: z.array(common_yard_areas_GeoPoint),
    id: z.number().int(),
    name: z.string(),
    parent_area_id: z.union([z.number(), z.null()]),
    reservations: z.array(common_yard_areas_AreaStationReservation),
    station_entry_points: z.array(common_yard_areas_AreaStationEntryPoint),
    stations: z.array(common_yard_areas_AreaStation),
    time_interval_end: z.unknown().transform(transformUnknownToDateTime).nullable(),
    time_interval_start: z.unknown().transform(transformUnknownToDateTime).nullable(),
});
const calculations_schedule_assembly_activities_AssemblySchedulingResultGET = z.object({}).partial();
const calculations_visualize_geometry_ServeVisualizationFileSchema = z.object({}).partial();
const charts_production_plan_batch_ProductionPlanBatchSchema = z
    .object({
        activities: z.array(common_activities_Activity),
        consumable_resources: z.array(common_consumable_resources_ConsumableResource),
        total_length: z.number(),
    })
    .partial();
const charts_production_plan_batch_RequestParameters = z.object({
    schedule_id: z.union([z.number(), z.null()]).optional().default(null),
    station_id: z.number().int(),
    timestamp: z.unknown().transform(transformUnknownToDateTime),
});
const charts_project_timeline_schedules_OverlayChartItem = z
    .object({
        activity_id: z.number().int(),
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        id: z.number().int(),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        time_delta: z.union([z.number(), z.null()]),
    })
    .partial();
const charts_project_timeline_schedules_OverlayFlowLine = z
    .object({
        end_activity_id: z.number().int(),
        start_activity_id: z.number().int(),
        timedelta: z.number().int(),
    })
    .partial();
const charts_project_timeline_schedules_ProjectTimelineArgs = z.object({ batch_id: z.number().int() }).partial();
const charts_project_timeline_schedules_ProjectTimelineSchedule = z
    .object({
        chart_items: z.array(charts_project_timeline_schedules_OverlayChartItem),
        flow_lines: z.array(charts_project_timeline_schedules_OverlayFlowLine),
    })
    .partial();
const charts_project_timelines_ChartItem = z
    .object({
        activity_category_id: z.union([z.number(), z.null()]),
        activity_category_name: z.union([z.string(), z.null()]),
        activity_template_external_id: z.string(),
        activity_template_id: z.number().int(),
        activity_template_name: z.string(),
        batch_id: z.number().int(),
        batch_name: z.string(),
        category_id: z.string(),
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        id: z.number().int(),
        is_artificial: z.boolean(),
        is_group: z.boolean(),
        is_highlighted: z.boolean(),
        is_visible: z.boolean(),
        name: z.union([z.string(), z.null()]),
        on_critical_path: z.boolean(),
        operation_type_id: z.number().int(),
        production_type_key: z.nativeEnum(ProductionTypes),
        progress: z.number(),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        station_id: z.union([z.number(), z.null()]),
        station_name: z.union([z.string(), z.null()]),
        time_delta: z.union([z.number(), z.null()]),
    })
    .partial();
const charts_project_timelines_Category = z
    .object({
        external_id: z.string(),
        id: z.string(),
        is_highlighted: z.boolean(),
        items: z.array(charts_project_timelines_ChartItem),
        name: z.string(),
        parent_category_id: z.union([z.string(), z.null()]),
        production_type_key: z.nativeEnum(ProductionTypes),
    })
    .partial();
const charts_project_timelines_ChartFlowLines = z
    .object({
        end_activity_id: z.number().int(),
        is_highlighted: z.boolean(),
        is_visible: z.boolean(),
        source_pos: z.number(),
        start_activity_id: z.number().int(),
        target_pos: z.number(),
        timedelta: z.number().int(),
    })
    .partial();
const charts_project_timelines_MilestoneMini = z
    .object({
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        id: z.number().int(),
        name: z.string(),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
    })
    .partial();
const charts_project_timelines_ProjectTimeline = z
    .object({
        categories: z.array(charts_project_timelines_Category),
        flow_lines: z.array(charts_project_timelines_ChartFlowLines),
        milestones: z.array(charts_project_timelines_MilestoneMini),
        visible_category_ids: z.array(z.string()),
    })
    .partial();
const charts_project_timelines_ProjectTimelineArgs = z
    .object({
        decisive_end_date: z.string(),
        decisive_start_date: z.string(),
        department_ext_id: z.string(),
        external_id: z.string(),
        highlight_activity_template_id: z.number().int(),
        highlight_consumable_resource_id: z.number().int(),
        label_id: z.array(z.string()),
        main_area: z.string(),
        name: z.string(),
        production_type_key: z.nativeEnum(ProductionTypes),
        progress: z.string(),
        required_date: z.string(),
        station_name: z.string(),
        sub_area: z.string(),
        sub_discipline: z.string(),
    })
    .partial();
const charts_schedule_chart_categories_StationActivity = z
    .object({
        color: z.string(),
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        err_flags: z.number().int(),
        id: z.number().int(),
        name: z.string(),
        num_tranches: z.number().int(),
        progress: z.number(),
        pt_key: z.nativeEnum(ProductionTypes),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        time_delta: z.union([z.number(), z.null()]),
        tranche_index: z.number().int(),
        visible: z.boolean(),
    })
    .partial();
const charts_schedule_chart_categories_Category = z
    .object({
        id: z.number().int(),
        items: z.array(charts_schedule_chart_categories_StationActivity),
        name: z.string(),
    })
    .partial();
const charts_schedule_chart_categories_ScheduleChartRequestArgs = z
    .object({
        category_name: z.string(),
        decisive_end_date: z.string(),
        decisive_start_date: z.string(),
        department_ext_id: z.union([z.string(), z.null()]).default(null),
        external_id: z.union([z.string(), z.null()]).default(null),
        include_archived: z.boolean().default(false),
        main_area: z.union([z.string(), z.null()]).default(null),
        name: z.string().default(""),
        production_type_key: z.nativeEnum(ProductionTypes),
        progress: z.union([z.string(), z.null()]).default(null),
        project_id: z.union([z.string(), z.null()]).default(null),
        project_name: z.union([z.string(), z.null()]).default(null),
        required_date: z.string(),
        station_name: z.union([z.string(), z.null()]).default(null),
        sub_area: z.union([z.string(), z.null()]).default(null),
        sub_discipline: z.union([z.string(), z.null()]).default(null),
        uniform_tranche_count: z.boolean().default(false),
    })
    .partial();
const charts_schedule_chart_categories_ScheduleChartScheduleRequestArgs = z
    .object({
        category_name: z.string(),
        decisive_end_date: z.string(),
        decisive_start_date: z.string(),
        department_ext_id: z.union([z.string(), z.null()]).default(null),
        external_id: z.union([z.string(), z.null()]).default(null),
        include_archived: z.boolean().default(false),
        main_area: z.union([z.string(), z.null()]).default(null),
        name: z.string().default(""),
        production_type_key: z.nativeEnum(ProductionTypes),
        progress: z.union([z.string(), z.null()]).default(null),
        project_id: z.union([z.string(), z.null()]).default(null),
        project_name: z.union([z.string(), z.null()]).default(null),
        required_date: z.string(),
        schedule_id: z.number().int(),
        station_name: z.union([z.string(), z.null()]).default(null),
        sub_area: z.union([z.string(), z.null()]).default(null),
        sub_discipline: z.union([z.string(), z.null()]).default(null),
        uniform_tranche_count: z.boolean().default(false),
    })
    .partial();
const charts_schedule_chart_categories_ScheduleChartSchema = z
    .object({ categories: z.array(charts_schedule_chart_categories_Category) })
    .partial();
const common_activity_categorization_group_rules_ActivityCategorizationGroupRuleGET = z.object({
    category_id: z.number().int(),
    id: z.number().int(),
    operator: z.string(),
    params: z.string(),
    selector: z.string(),
    target_key: z.string(),
});
const common_activity_relationships_ActivityRelationshipItem = z
    .object({
        activity_external_id: z.string(),
        activity_id: z.number().int(),
        activity_name: z.string(),
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        id: z.number().int(),
        offset: z.number(),
        production_type_key: z.nativeEnum(ProductionTypes),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        source_pos: z.number(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        target_pos: z.number(),
        time_delta: z.number(),
    })
    .partial();
const common_activity_relationships_ActivityRelationships = z
    .object({
        predecessors: z.array(common_activity_relationships_ActivityRelationshipItem),
        successors: z.array(common_activity_relationships_ActivityRelationshipItem),
    })
    .partial();
const common_activity_relationships_ScheduledActivityRelationshipItem = z
    .object({
        activity_external_id: z.string(),
        activity_id: z.number().int(),
        activity_name: z.string(),
        due_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        id: z.number().int(),
        offset: z.number(),
        production_type_key: z.nativeEnum(ProductionTypes),
        real_end_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        real_start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        release_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        required_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        schedule_values: common_activities_ScheduleValues,
        source_pos: z.number(),
        start_date: z.unknown().transform(transformUnknownToDateTime).nullable(),
        target_pos: z.number(),
        time_delta: z.number(),
    })
    .partial();
const common_activity_relationships_ScheduledActivityRelationships = z
    .object({
        predecessors: z.array(common_activity_relationships_ScheduledActivityRelationshipItem),
        successors: z.array(common_activity_relationships_ScheduledActivityRelationshipItem),
    })
    .partial();
const common_activity_templates_ActivityTemplateResourceLink = z.object({
    consumable_resource_id: z.number().int(),
    consumable_resource_name: z.string().optional(),
    id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    quantity: z.number().int().optional().default(1),
});
const common_activity_templates_PrecedenceLink = z.object({
    id: z.number().int().optional(),
    required_activity_template_id: z.number().int(),
    required_activity_template_name: z.string().optional(),
});
const common_activity_templates_ActivityTemplatePOST = z.object({
    activity_category_id: z.union([z.number(), z.null()]).optional(),
    activity_category_name: z.union([z.string(), z.null()]).optional(),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int().optional(),
    input_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLink).optional(),
    labels: z.array(common_labels_LabelMini).optional(),
    man_hours: z.number(),
    name: z.string(),
    output_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLink).optional(),
    parent_id: z.union([z.number(), z.null()]),
    precedences: z.array(common_activity_templates_PrecedenceLink).optional(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int(),
    project_name: z.string().optional(),
});
const common_activity_templates_ActivityTemplates = z.object({
    activity_category_id: z.union([z.number(), z.null()]).optional(),
    activity_category_name: z.union([z.string(), z.null()]).optional(),
    duration: z.number(),
    external_id: z.string(),
    id: z.number().int(),
    input_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLink).optional(),
    labels: z.array(common_labels_LabelMini).optional(),
    man_hours: z.number(),
    name: z.string(),
    output_resource_links: z.array(common_activity_templates_ActivityTemplateResourceLink).optional(),
    parent_id: z.union([z.number(), z.null()]),
    precedences: z.array(common_activity_templates_PrecedenceLink).optional(),
    production_type_id: z.number().int().optional(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
    production_type_name: z.string().optional(),
    project_color: z.string().optional(),
    project_id: z.number().int(),
    project_name: z.string().optional(),
});
const common_operation_logs_LogEntry = z.object({ level: z.number().int(), message: z.string() }).partial();
const common_operation_logs_Metadata = z
    .object({
        error_count: z.number().int(),
        messages: z.array(common_operation_logs_LogEntry),
        retry_with_force_is_possible: z.boolean(),
        warning_count: z.number().int(),
    })
    .partial();
const common_operation_logs_GlobalOperationLog = z
    .object({
        activity_key: z.string(),
        end_time: z.unknown().transform(transformUnknownToDateTime),
        id: z.number().int(),
        message: z.string(),
        metadata: common_operation_logs_Metadata,
        start_time: z.unknown().transform(transformUnknownToDateTime),
        status_code: z.number().int(),
        username: z.string(),
    })
    .partial();
const common_operation_logs_LogEntryGET = z.object({
    level: z.number().int(),
    message: z.string(),
});
const common_operation_logs_MetadataGET = z.object({
    error_count: z.number().int(),
    messages: z.array(common_operation_logs_LogEntryGET),
    retry_with_force_is_possible: z.boolean(),
    warning_count: z.number().int(),
});
const common_operation_logs_GlobalOperationLogGET = z.object({
    activity_key: z.string(),
    end_time: z.unknown().transform(transformUnknownToDateTime),
    id: z.number().int(),
    message: z.string(),
    metadata: common_operation_logs_MetadataGET,
    start_time: z.unknown().transform(transformUnknownToDateTime),
    status_code: z.number().int(),
    username: z.string(),
});
const common_operation_logs_MetadataMini = z
    .object({ error_count: z.number().int(), warning_count: z.number().int() })
    .partial();
const common_operation_logs_GlobalOperationLogMini = z
    .object({
        activity_key: z.string(),
        end_time: z.unknown().transform(transformUnknownToDateTime),
        id: z.number().int(),
        message: z.string(),
        metadata: common_operation_logs_MetadataMini,
        start_time: z.unknown().transform(transformUnknownToDateTime),
        status_code: z.number().int(),
        username: z.string(),
    })
    .partial();
const common_operation_logs_LogExportTofile = z.object({}).partial();
const common_production_types_ProductionType = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
});
const common_production_types_operation_types_ProductionTypesOperationTypes = z.object({
    id: z.number().int(),
    key: z.string(),
    name: z.string(),
    operation_types: z.array(common_operation_types_OperationType).optional(),
});
const common_station_yard_area_reservations_StationYardAreaReservation = z.object({
    available_from: z.unknown().transform(transformUnknownToDateTime).nullable(),
    available_to: z.unknown().transform(transformUnknownToDateTime).nullable(),
    id: z.number().int().optional(),
    project_color: z.union([z.string(), z.null()]).optional(),
    project_id: z.union([z.number(), z.null()]),
    project_name: z.union([z.string(), z.null()]).optional(),
    yard_area_id: z.number().int(),
    yard_area_name: z.string().optional(),
});
const common_update_activities_ResponseActivityMappingGET = z.object({
    activity_template_id: z.number().int(),
    batch_id: z.number().int(),
});
const common_update_activities_UpdateActivityResponseGET = z.object({
    added_activities: z.array(common_update_activities_ResponseActivityMappingGET),
    production_type_key: z.nativeEnum(ProductionTypes),
    project_id: z.number().int(),
    removed_activities: z.array(common_update_activities_ResponseActivityMappingGET),
});
const common_update_yard_areas_UpdateYardAreaResponseGET = z.object({
    status: z.string(),
});
const data_import_formats_Column = z
    .object({
        example_value: z.string(),
        header: z.string(),
        is_processed: z.boolean(),
        note: z.string(),
        required: z.boolean(),
    })
    .partial();
const data_import_formats_ImportDataFormat = z
    .object({ columns: z.array(data_import_formats_Column), note: z.string() })
    .partial();
const data_import_activity_data_ActivityDataImportFormatSchema = z
    .object({
        mips: data_import_formats_ImportDataFormat,
        safran: data_import_formats_ImportDataFormat,
    })
    .partial();
const data_import_activity_data_ActivityDataImportResponseGET = z.object({
    message: z.string(),
    operation_id: z.number().int(),
});
const data_import_animation_data_AnimationDataImportResponseGET = z.object({
    message: z.string(),
    operation_id: z.number().int(),
});
const data_import_future_project_FutureProjectDataImportFormatSchema = z
    .object({
        cajac: data_import_formats_ImportDataFormat,
        production_families: data_import_formats_ImportDataFormat,
        sequence: data_import_formats_ImportDataFormat,
    })
    .partial();
const data_import_future_project_FutureProjectDataImportResponseGET = z.object({
    message: z.string(),
    operation_id: z.number().int(),
});
const data_import_vpl_planning_data_VPLPlanningDataImportFormatSchema = z
    .object({
        vpl1: data_import_formats_ImportDataFormat,
        vpl2: data_import_formats_ImportDataFormat,
    })
    .partial();
const data_import_vpl_planning_data_VPLPlanningDataImportResponseGET = z.object({
    message: z.string(),
    operation_id: z.number().int(),
});
const filters_activity_templates_ActivityTemplatesFilter = z.object({
    external_id: z.string(),
    id: z.number().int(),
    name: z.string(),
    production_type_key: z.nativeEnum(ProductionTypes).optional(),
});
const apiserverfoundation_authentication_authentication_resources_AuthMainSchema = z.object({ login: z.string() });
const apiserverfoundation_authentication_oauth2_authorize_AuthorizeSchema = z.object({}).partial();
const apiserverfoundation_authentication_oauth2_callback_CallbackSchema = z.object({}).partial();
const apiserverfoundation_authentication_options_AuthOptionSchema = z
    .object({
        label: z.union([z.string(), z.null()]),
        provider_type: z.enum(["PASSWORD", "SSO"]),
        url: z.union([z.string(), z.null()]),
    })
    .partial();
const apiserverfoundation_authentication_options_OptionsSchema = z.object({
    options: z.array(apiserverfoundation_authentication_options_AuthOptionSchema),
});
const apiserverfoundation_authentication_password_resource_PasswordPutResponseSchemaGET = z.object({
    changed: z.boolean(),
    message: z.string(),
});
const apiserverfoundation_authentication_status_PasswordSchema = z.object({
    is_active: z.boolean(),
    is_admin: z.boolean(),
    is_external_login: z.boolean(),
    login: z.string(),
});
const apiserverfoundation_management_api_doc_ApiDocSchema = z.object({}).partial();
const apiserverfoundation_management_app_version_VersionSchema = z.object({
    server_revision: z.string(),
    server_version: z.string(),
});
const apiserverfoundation_management_configuration_ConfigurationSchema = z.object({ config: z.unknown() }).partial();
const apiserverfoundation_management_data_scope_FullDataScope = z
    .object({ id: z.number().int(), key: z.number().int(), name: z.string() })
    .partial();
const apiserverfoundation_management_resend_activation_email_ResendActivationEmailSchemaGET = z.object({}).partial();
const apiserverfoundation_management_reset_password_ResetPasswordResponseSchemaGET = z.object({
    message: z.string(),
    status: z.enum(["FAILURE", "SUCCESS"]),
});
const calendars_api_countries_Subdiv = z.object({ code: z.string(), name: z.string() }).partial();
const calendars_api_countries_Country = z
    .object({
        code: z.string(),
        name: z.string(),
        subdivs: z.array(calendars_api_countries_Subdiv),
    })
    .partial();
const calendars_api_materialized_calendar_Segment = z.object({
    end: z.unknown().transform(transformUnknownToDateTime),
    note: z.union([z.string(), z.null()]).optional().default(null),
    shift_id: z.number().int(),
    start: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_materialized_calendar_MaterializedCalendarStretch = z.object({
    calendar_pattern_id: z.number().int(),
    calendar_pattern_name: z.string().optional(),
    id: z.number().int().optional(),
    note: z.union([z.string(), z.null()]).optional().default(null),
    public_holiday_calendar_id: z.union([z.number(), z.null()]).optional().default(null),
    public_holiday_calendar_name: z.union([z.string(), z.null()]).optional(),
    segments: z.array(calendars_api_materialized_calendar_Segment),
    start_time: z.unknown().transform(transformUnknownToDateTime),
});
const calendars_api_materialized_calendar_MaterializedCalendar = z.object({
    id: z.number().int(),
    name: z.string(),
    stretches: z.array(calendars_api_materialized_calendar_MaterializedCalendarStretch),
    timezone: z.string(),
});
const calendars_api_materialized_calendar_MaterializedCalendarParams = z.object({
    end_date: z.unknown().transform(transformUnknownToDateTime),
    start_date: z.unknown().transform(transformUnknownToDateTime),
});
const oplog_web_api_operation_logs_Metadata = z
    .object({
        messages: z.array(z.object({}).partial().passthrough()),
        retry_with_force_is_possible: z.boolean(),
    })
    .partial();
const oplog_web_api_operation_logs_GlobalOperationLog = z
    .object({
        activity_key: z.string(),
        end_time: z.unknown().transform(transformUnknownToDateTime),
        id: z.number().int(),
        message: z.string(),
        metadata: oplog_web_api_operation_logs_Metadata,
        start_time: z.unknown().transform(transformUnknownToDateTime),
        status_code: z.number().int(),
        username: z.string(),
    })
    .partial();
const oplog_web_api_templates_Template = z.object({ code: z.number().int(), template: z.string() }).partial();

export {
    apiserverfoundation_authentication_authentication_resources_AuthMainSchemaGET as generatedApiserverfoundationAuthenticationAuthenticationResourcesAuthMainSchemaGETSchema,
    apiserverfoundation_authentication_authentication_resources_AuthMainSchema as generatedApiserverfoundationAuthenticationAuthenticationResourcesAuthMainSchemaSchema,
    apiserverfoundation_authentication_authentication_resources_AuthResultSchemaGET as generatedApiserverfoundationAuthenticationAuthenticationResourcesAuthResultSchemaGETSchema,
    apiserverfoundation_authentication_authentication_resources_AuthResultSchema as generatedApiserverfoundationAuthenticationAuthenticationResourcesAuthResultSchemaSchema,
    apiserverfoundation_authentication_authentication_resources_LoginSchema as generatedApiserverfoundationAuthenticationAuthenticationResourcesLoginSchemaSchema,
    apiserverfoundation_authentication_oauth2_authorize_AuthorizeSchemaGET as generatedApiserverfoundationAuthenticationOauth2AuthorizeAuthorizeSchemaGETSchema,
    apiserverfoundation_authentication_oauth2_authorize_AuthorizeSchema as generatedApiserverfoundationAuthenticationOauth2AuthorizeAuthorizeSchemaSchema,
    apiserverfoundation_authentication_oauth2_callback_CallbackSchemaGET as generatedApiserverfoundationAuthenticationOauth2CallbackCallbackSchemaGETSchema,
    apiserverfoundation_authentication_oauth2_callback_CallbackSchema as generatedApiserverfoundationAuthenticationOauth2CallbackCallbackSchemaSchema,
    apiserverfoundation_authentication_options_AuthOptionSchemaGET as generatedApiserverfoundationAuthenticationOptionsAuthOptionSchemaGETSchema,
    apiserverfoundation_authentication_options_AuthOptionSchema as generatedApiserverfoundationAuthenticationOptionsAuthOptionSchemaSchema,
    apiserverfoundation_authentication_options_OptionsSchemaGET as generatedApiserverfoundationAuthenticationOptionsOptionsSchemaGETSchema,
    apiserverfoundation_authentication_options_OptionsSchema as generatedApiserverfoundationAuthenticationOptionsOptionsSchemaSchema,
    apiserverfoundation_authentication_password_resource_PasswordPutRequestSchema as generatedApiserverfoundationAuthenticationPasswordResourcePasswordPutRequestSchemaSchema,
    apiserverfoundation_authentication_password_resource_PasswordPutResponseSchemaGET as generatedApiserverfoundationAuthenticationPasswordResourcePasswordPutResponseSchemaGETSchema,
    apiserverfoundation_authentication_password_resource_PasswordPutResponseSchema as generatedApiserverfoundationAuthenticationPasswordResourcePasswordPutResponseSchemaSchema,
    apiserverfoundation_authentication_status_PasswordSchemaGET as generatedApiserverfoundationAuthenticationStatusPasswordSchemaGETSchema,
    apiserverfoundation_authentication_status_PasswordSchema as generatedApiserverfoundationAuthenticationStatusPasswordSchemaSchema,
    apiserverfoundation_management_api_doc_ApiDocSchemaGET as generatedApiserverfoundationManagementApiDocApiDocSchemaGETSchema,
    apiserverfoundation_management_api_doc_ApiDocSchema as generatedApiserverfoundationManagementApiDocApiDocSchemaSchema,
    apiserverfoundation_management_app_version_VersionSchemaGET as generatedApiserverfoundationManagementAppVersionVersionSchemaGETSchema,
    apiserverfoundation_management_app_version_VersionSchema as generatedApiserverfoundationManagementAppVersionVersionSchemaSchema,
    apiserverfoundation_management_configuration_ConfigurationSchemaGET as generatedApiserverfoundationManagementConfigurationConfigurationSchemaGETSchema,
    apiserverfoundation_management_configuration_ConfigurationSchema as generatedApiserverfoundationManagementConfigurationConfigurationSchemaSchema,
    apiserverfoundation_management_current_user_info_ComponentPermissionSchemaGET as generatedApiserverfoundationManagementCurrentUserInfoComponentPermissionSchemaGETSchema,
    apiserverfoundation_management_current_user_info_ComponentPermissionSchema as generatedApiserverfoundationManagementCurrentUserInfoComponentPermissionSchemaSchema,
    apiserverfoundation_management_current_user_info_CurrentUserInfoSchemaGET as generatedApiserverfoundationManagementCurrentUserInfoCurrentUserInfoSchemaGETSchema,
    apiserverfoundation_management_current_user_info_CurrentUserInfoSchema as generatedApiserverfoundationManagementCurrentUserInfoCurrentUserInfoSchemaSchema,
    apiserverfoundation_management_current_user_info_UserRoleSchemaGET as generatedApiserverfoundationManagementCurrentUserInfoUserRoleSchemaGETSchema,
    apiserverfoundation_management_current_user_info_UserRoleSchema as generatedApiserverfoundationManagementCurrentUserInfoUserRoleSchemaSchema,
    apiserverfoundation_management_data_scope_DataScopeGET as generatedApiserverfoundationManagementDataScopeDataScopeGETSchema,
    apiserverfoundation_management_data_scope_DataScope as generatedApiserverfoundationManagementDataScopeDataScopeSchema,
    apiserverfoundation_management_data_scope_DataScopeSchemaGET as generatedApiserverfoundationManagementDataScopeDataScopeSchemaGETSchema,
    apiserverfoundation_management_data_scope_DataScopeSchemaPOST as generatedApiserverfoundationManagementDataScopeDataScopeSchemaPOSTSchema,
    apiserverfoundation_management_data_scope_DataScopeSchema as generatedApiserverfoundationManagementDataScopeDataScopeSchemaSchema,
    apiserverfoundation_management_data_scope_DataScopeSettingGET as generatedApiserverfoundationManagementDataScopeDataScopeSettingGETSchema,
    apiserverfoundation_management_data_scope_DataScopeSetting as generatedApiserverfoundationManagementDataScopeDataScopeSettingSchema,
    apiserverfoundation_management_data_scope_FullDataScopeGET as generatedApiserverfoundationManagementDataScopeFullDataScopeGETSchema,
    apiserverfoundation_management_data_scope_FullDataScope as generatedApiserverfoundationManagementDataScopeFullDataScopeSchema,
    apiserverfoundation_management_resend_activation_email_ResendActivationEmailSchemaGET as generatedApiserverfoundationManagementResendActivationEmailResendActivationEmailSchemaGETSchema,
    apiserverfoundation_management_resend_activation_email_ResendActivationEmailSchema as generatedApiserverfoundationManagementResendActivationEmailResendActivationEmailSchemaSchema,
    apiserverfoundation_management_reset_password_ResetPasswordResponseSchemaGET as generatedApiserverfoundationManagementResetPasswordResetPasswordResponseSchemaGETSchema,
    apiserverfoundation_management_reset_password_ResetPasswordResponseSchema as generatedApiserverfoundationManagementResetPasswordResetPasswordResponseSchemaSchema,
    apiserverfoundation_management_reset_password_ResetPasswordSchema as generatedApiserverfoundationManagementResetPasswordResetPasswordSchemaSchema,
    apiserverfoundation_management_roles_ComponentPermissionSchemaGET as generatedApiserverfoundationManagementRolesComponentPermissionSchemaGETSchema,
    apiserverfoundation_management_roles_ComponentPermissionSchema as generatedApiserverfoundationManagementRolesComponentPermissionSchemaSchema,
    apiserverfoundation_management_roles_EndpointInfoSchemaGET as generatedApiserverfoundationManagementRolesEndpointInfoSchemaGETSchema,
    apiserverfoundation_management_roles_EndpointInfoSchema as generatedApiserverfoundationManagementRolesEndpointInfoSchemaSchema,
    apiserverfoundation_management_roles_RoleSchemaGET as generatedApiserverfoundationManagementRolesRoleSchemaGETSchema,
    apiserverfoundation_management_roles_RoleSchemaPOST as generatedApiserverfoundationManagementRolesRoleSchemaPOSTSchema,
    apiserverfoundation_management_roles_RoleSchema as generatedApiserverfoundationManagementRolesRoleSchemaSchema,
    apiserverfoundation_management_user_permissions_CurrentUserPermissionsSchemaGET as generatedApiserverfoundationManagementUserPermissionsCurrentUserPermissionsSchemaGETSchema,
    apiserverfoundation_management_users_DataScopeSchemaGET as generatedApiserverfoundationManagementUsersDataScopeSchemaGETSchema,
    apiserverfoundation_management_users_DataScopeSchema as generatedApiserverfoundationManagementUsersDataScopeSchemaSchema,
    apiserverfoundation_management_user_settings_UserSettingsRequestPutSchema as generatedApiserverfoundationManagementUserSettingsUserSettingsRequestPutSchemaSchema,
    apiserverfoundation_management_user_settings_UserSettingsSchemaGET as generatedApiserverfoundationManagementUserSettingsUserSettingsSchemaGETSchema,
    apiserverfoundation_management_user_settings_UserSettingsSchema as generatedApiserverfoundationManagementUserSettingsUserSettingsSchemaSchema,
    apiserverfoundation_management_users_SimpleRoleSchemaGET as generatedApiserverfoundationManagementUsersSimpleRoleSchemaGETSchema,
    apiserverfoundation_management_users_SimpleRoleSchema as generatedApiserverfoundationManagementUsersSimpleRoleSchemaSchema,
    apiserverfoundation_management_users_UsersSchemaGET as generatedApiserverfoundationManagementUsersUsersSchemaGETSchema,
    apiserverfoundation_management_users_UsersSchemaPOST as generatedApiserverfoundationManagementUsersUsersSchemaPOSTSchema,
    apiserverfoundation_management_users_UsersSchema as generatedApiserverfoundationManagementUsersUsersSchemaSchema,
    calculations_schedule_activities_AssemblyParamsGET as generatedCalculationsScheduleActivitiesAssemblyParamsGETSchema,
    calculations_schedule_activities_AssemblyParams as generatedCalculationsScheduleActivitiesAssemblyParamsSchema,
    calculations_schedule_activities_ScheduleActivitiesProcessStatusGET as generatedCalculationsScheduleActivitiesScheduleActivitiesProcessStatusGETSchema,
    calculations_schedule_activities_ScheduleActivitiesProcessStatus as generatedCalculationsScheduleActivitiesScheduleActivitiesProcessStatusSchema,
    calculations_schedule_activities_ScheduleActivitiesRequestGET as generatedCalculationsScheduleActivitiesScheduleActivitiesRequestGETSchema,
    calculations_schedule_activities_ScheduleActivitiesRequest as generatedCalculationsScheduleActivitiesScheduleActivitiesRequestSchema,
    calculations_schedule_activities_VPLParamsGET as generatedCalculationsScheduleActivitiesVPLParamsGETSchema,
    calculations_schedule_activities_VPLParams as generatedCalculationsScheduleActivitiesVPLParamsSchema,
    calculations_schedule_assembly_activities_AssemblySchedulingRequest as generatedCalculationsScheduleAssemblyActivitiesAssemblySchedulingRequestSchema,
    calculations_schedule_assembly_activities_AssemblySchedulingResultGET as generatedCalculationsScheduleAssemblyActivitiesAssemblySchedulingResultGETSchema,
    calculations_schedule_assembly_activities_AssemblySchedulingResult as generatedCalculationsScheduleAssemblyActivitiesAssemblySchedulingResultSchema,
    calculations_schedule_production_lines_ProcessRequestGET as generatedCalculationsScheduleProductionLinesProcessRequestGETSchema,
    calculations_schedule_production_lines_ProcessRequest as generatedCalculationsScheduleProductionLinesProcessRequestSchema,
    calculations_schedule_production_lines_ProcessStatusGET as generatedCalculationsScheduleProductionLinesProcessStatusGETSchema,
    calculations_schedule_production_lines_ProcessStatus as generatedCalculationsScheduleProductionLinesProcessStatusSchema,
    calculations_visualize_geometry_ServeVisualizationFileSchemaGET as generatedCalculationsVisualizeGeometryServeVisualizationFileSchemaGETSchema,
    calculations_visualize_geometry_ServeVisualizationFileSchema as generatedCalculationsVisualizeGeometryServeVisualizationFileSchemaSchema,
    calculations_visualize_geometry_VisualizeGeometryRequestGET as generatedCalculationsVisualizeGeometryVisualizeGeometryRequestGETSchema,
    calculations_visualize_geometry_VisualizeGeometryRequestProcessStatusGET as generatedCalculationsVisualizeGeometryVisualizeGeometryRequestProcessStatusGETSchema,
    calculations_visualize_geometry_VisualizeGeometryRequestProcessStatus as generatedCalculationsVisualizeGeometryVisualizeGeometryRequestProcessStatusSchema,
    calculations_visualize_geometry_VisualizeGeometryRequest as generatedCalculationsVisualizeGeometryVisualizeGeometryRequestSchema,
    calendars_api_calendar_patterns_CalendarPatternGET as generatedCalendarsApiCalendarPatternsCalendarPatternGETSchema,
    calendars_api_calendar_patterns_CalendarPatternPOST as generatedCalendarsApiCalendarPatternsCalendarPatternPOSTSchema,
    calendars_api_calendar_patterns_CalendarPattern as generatedCalendarsApiCalendarPatternsCalendarPatternSchema,
    calendars_api_calendar_patterns_ShiftGET as generatedCalendarsApiCalendarPatternsShiftGETSchema,
    calendars_api_calendar_patterns_Shift as generatedCalendarsApiCalendarPatternsShiftSchema,
    calendars_api_calendars_CalendarGET as generatedCalendarsApiCalendarsCalendarGETSchema,
    calendars_api_calendars_CalendarPOST as generatedCalendarsApiCalendarsCalendarPOSTSchema,
    calendars_api_calendars_Calendar as generatedCalendarsApiCalendarsCalendarSchema,
    calendars_api_calendars_CalendarStretchGET as generatedCalendarsApiCalendarsCalendarStretchGETSchema,
    calendars_api_calendars_CalendarStretch as generatedCalendarsApiCalendarsCalendarStretchSchema,
    calendars_api_countries_CountryGET as generatedCalendarsApiCountriesCountryGETSchema,
    calendars_api_countries_Country as generatedCalendarsApiCountriesCountrySchema,
    calendars_api_countries_SubdivGET as generatedCalendarsApiCountriesSubdivGETSchema,
    calendars_api_countries_Subdiv as generatedCalendarsApiCountriesSubdivSchema,
    calendars_api_materialized_calendar_MaterializedCalendarGET as generatedCalendarsApiMaterializedCalendarMaterializedCalendarGETSchema,
    calendars_api_materialized_calendar_MaterializedCalendarParams as generatedCalendarsApiMaterializedCalendarMaterializedCalendarParamsSchema,
    calendars_api_materialized_calendar_MaterializedCalendar as generatedCalendarsApiMaterializedCalendarMaterializedCalendarSchema,
    calendars_api_materialized_calendar_MaterializedCalendarStretchGET as generatedCalendarsApiMaterializedCalendarMaterializedCalendarStretchGETSchema,
    calendars_api_materialized_calendar_MaterializedCalendarStretch as generatedCalendarsApiMaterializedCalendarMaterializedCalendarStretchSchema,
    calendars_api_materialized_calendar_SegmentGET as generatedCalendarsApiMaterializedCalendarSegmentGETSchema,
    calendars_api_materialized_calendar_Segment as generatedCalendarsApiMaterializedCalendarSegmentSchema,
    calendars_api_public_holiday_calendars_PublicHolidayCalendarGET as generatedCalendarsApiPublicHolidayCalendarsPublicHolidayCalendarGETSchema,
    calendars_api_public_holiday_calendars_PublicHolidayCalendarPOST as generatedCalendarsApiPublicHolidayCalendarsPublicHolidayCalendarPOSTSchema,
    calendars_api_public_holiday_calendars_PublicHolidayCalendar as generatedCalendarsApiPublicHolidayCalendarsPublicHolidayCalendarSchema,
    calendars_api_public_holiday_calendars_PublicHolidayGET as generatedCalendarsApiPublicHolidayCalendarsPublicHolidayGETSchema,
    calendars_api_public_holiday_calendars_PublicHoliday as generatedCalendarsApiPublicHolidayCalendarsPublicHolidaySchema,
    charts_flow_graphs_FlowEdgeGET as generatedChartsFlowGraphsFlowEdgeGETSchema,
    charts_flow_graphs_FlowEdge as generatedChartsFlowGraphsFlowEdgeSchema,
    charts_flow_graphs_FlowGraphDetailGET as generatedChartsFlowGraphsFlowGraphDetailGETSchema,
    charts_flow_graphs_FlowGraphDetailPOST as generatedChartsFlowGraphsFlowGraphDetailPOSTSchema,
    charts_flow_graphs_FlowGraphDetail as generatedChartsFlowGraphsFlowGraphDetailSchema,
    charts_flow_graphs_FlowGraphGET as generatedChartsFlowGraphsFlowGraphGETSchema,
    charts_flow_nodes_FlowNodeGET as generatedChartsFlowNodesFlowNodeGETSchema,
    charts_flow_nodes_FlowNodePOST as generatedChartsFlowNodesFlowNodePOSTSchema,
    charts_flow_nodes_FlowNode as generatedChartsFlowNodesFlowNodeSchema,
    charts_graph_shapes_GraphShapeGET as generatedChartsGraphShapesGraphShapeGETSchema,
    charts_graph_shapes_GraphShapePOST as generatedChartsGraphShapesGraphShapePOSTSchema,
    charts_graph_shapes_GraphShape as generatedChartsGraphShapesGraphShapeSchema,
    charts_production_plan_batch_ProductionPlanBatchSchemaGET as generatedChartsProductionPlanBatchProductionPlanBatchSchemaGETSchema,
    charts_production_plan_batch_ProductionPlanBatchSchema as generatedChartsProductionPlanBatchProductionPlanBatchSchemaSchema,
    charts_production_plan_batch_RequestParameters as generatedChartsProductionPlanBatchRequestParametersSchema,
    charts_project_timelines_CategoryGET as generatedChartsProjectTimelinesCategoryGETSchema,
    charts_project_timelines_Category as generatedChartsProjectTimelinesCategorySchema,
    charts_project_timelines_ChartFlowLinesGET as generatedChartsProjectTimelinesChartFlowLinesGETSchema,
    charts_project_timelines_ChartFlowLines as generatedChartsProjectTimelinesChartFlowLinesSchema,
    charts_project_timelines_ChartItemGET as generatedChartsProjectTimelinesChartItemGETSchema,
    charts_project_timelines_ChartItem as generatedChartsProjectTimelinesChartItemSchema,
    charts_project_timeline_schedules_OverlayChartItemGET as generatedChartsProjectTimelineSchedulesOverlayChartItemGETSchema,
    charts_project_timeline_schedules_OverlayChartItem as generatedChartsProjectTimelineSchedulesOverlayChartItemSchema,
    charts_project_timeline_schedules_OverlayFlowLineGET as generatedChartsProjectTimelineSchedulesOverlayFlowLineGETSchema,
    charts_project_timeline_schedules_OverlayFlowLine as generatedChartsProjectTimelineSchedulesOverlayFlowLineSchema,
    charts_project_timeline_schedules_ProjectTimelineArgs as generatedChartsProjectTimelineSchedulesProjectTimelineArgsSchema,
    charts_project_timeline_schedules_ProjectTimelineScheduleGET as generatedChartsProjectTimelineSchedulesProjectTimelineScheduleGETSchema,
    charts_project_timeline_schedules_ProjectTimelineSchedule as generatedChartsProjectTimelineSchedulesProjectTimelineScheduleSchema,
    charts_project_timelines_MilestoneMiniGET as generatedChartsProjectTimelinesMilestoneMiniGETSchema,
    charts_project_timelines_MilestoneMini as generatedChartsProjectTimelinesMilestoneMiniSchema,
    charts_project_timelines_ProjectTimelineArgs as generatedChartsProjectTimelinesProjectTimelineArgsSchema,
    charts_project_timelines_ProjectTimelineGET as generatedChartsProjectTimelinesProjectTimelineGETSchema,
    charts_project_timelines_ProjectTimeline as generatedChartsProjectTimelinesProjectTimelineSchema,
    charts_schedule_chart_categories_CategoryGET as generatedChartsScheduleChartCategoriesCategoryGETSchema,
    charts_schedule_chart_categories_Category as generatedChartsScheduleChartCategoriesCategorySchema,
    charts_schedule_chart_categories_ScheduleChartRequestArgs as generatedChartsScheduleChartCategoriesScheduleChartRequestArgsSchema,
    charts_schedule_chart_categories_ScheduleChartScheduleRequestArgs as generatedChartsScheduleChartCategoriesScheduleChartScheduleRequestArgsSchema,
    charts_schedule_chart_categories_ScheduleChartSchemaGET as generatedChartsScheduleChartCategoriesScheduleChartSchemaGETSchema,
    charts_schedule_chart_categories_ScheduleChartSchema as generatedChartsScheduleChartCategoriesScheduleChartSchemaSchema,
    charts_schedule_chart_categories_StationActivityGET as generatedChartsScheduleChartCategoriesStationActivityGETSchema,
    charts_schedule_chart_categories_StationActivity as generatedChartsScheduleChartCategoriesStationActivitySchema,
    common_activities_ActivityGET as generatedCommonActivitiesActivityGETSchema,
    common_activities_Activity as generatedCommonActivitiesActivitySchema,
    common_activities_ScheduledActivityGET as generatedCommonActivitiesScheduledActivityGETSchema,
    common_activities_ScheduledActivity as generatedCommonActivitiesScheduledActivitySchema,
    common_activities_ScheduleValuesGET as generatedCommonActivitiesScheduleValuesGETSchema,
    common_activities_ScheduleValues as generatedCommonActivitiesScheduleValuesSchema,
    common_activity_categories_ActivityCategoryGET as generatedCommonActivityCategoriesActivityCategoryGETSchema,
    common_activity_categories_ActivityCategoryPOST as generatedCommonActivityCategoriesActivityCategoryPOSTSchema,
    common_activity_categories_ActivityCategory as generatedCommonActivityCategoriesActivityCategorySchema,
    common_activity_categorization_group_rules_ActivityCategorizationGroupRuleGET as generatedCommonActivityCategorizationGroupRulesActivityCategorizationGroupRuleGETSchema,
    common_activity_categorization_group_rules_ActivityCategorizationGroupRule as generatedCommonActivityCategorizationGroupRulesActivityCategorizationGroupRuleSchema,
    common_activity_categorization_rules_ActivityCategorizationRuleGET as generatedCommonActivityCategorizationRulesActivityCategorizationRuleGETSchema,
    common_activity_categorization_rules_ActivityCategorizationRulePOST as generatedCommonActivityCategorizationRulesActivityCategorizationRulePOSTSchema,
    common_activity_categorization_rules_ActivityCategorizationRule as generatedCommonActivityCategorizationRulesActivityCategorizationRuleSchema,
    common_activity_categorization_targets_ActivityCategorizationTargetGET as generatedCommonActivityCategorizationTargetsActivityCategorizationTargetGETSchema,
    common_activity_category_groups_ActivityCategoryGroupGET as generatedCommonActivityCategoryGroupsActivityCategoryGroupGETSchema,
    common_activity_category_groups_ActivityCategoryGroupPOST as generatedCommonActivityCategoryGroupsActivityCategoryGroupPOSTSchema,
    common_activity_category_groups_ActivityCategoryGroup as generatedCommonActivityCategoryGroupsActivityCategoryGroupSchema,
    common_activity_relationships_ActivityRelationshipItemGET as generatedCommonActivityRelationshipsActivityRelationshipItemGETSchema,
    common_activity_relationships_ActivityRelationshipItem as generatedCommonActivityRelationshipsActivityRelationshipItemSchema,
    common_activity_relationships_ActivityRelationshipsGET as generatedCommonActivityRelationshipsActivityRelationshipsGETSchema,
    common_activity_relationships_ActivityRelationships as generatedCommonActivityRelationshipsActivityRelationshipsSchema,
    common_activity_relationships_ScheduledActivityRelationshipItemGET as generatedCommonActivityRelationshipsScheduledActivityRelationshipItemGETSchema,
    common_activity_relationships_ScheduledActivityRelationshipItem as generatedCommonActivityRelationshipsScheduledActivityRelationshipItemSchema,
    common_activity_relationships_ScheduledActivityRelationshipsGET as generatedCommonActivityRelationshipsScheduledActivityRelationshipsGETSchema,
    common_activity_relationships_ScheduledActivityRelationships as generatedCommonActivityRelationshipsScheduledActivityRelationshipsSchema,
    common_activity_template_detail_ActivityTemplateConsumableResourceRequirementGET as generatedCommonActivityTemplateDetailActivityTemplateConsumableResourceRequirementGETSchema,
    common_activity_template_detail_ActivityTemplateConsumableResourceRequirement as generatedCommonActivityTemplateDetailActivityTemplateConsumableResourceRequirementSchema,
    common_activity_template_detail_ActivityTemplateDetailGET as generatedCommonActivityTemplateDetailActivityTemplateDetailGETSchema,
    common_activity_template_detail_ActivityTemplateDetailPOST as generatedCommonActivityTemplateDetailActivityTemplateDetailPOSTSchema,
    common_activity_template_detail_ActivityTemplateDetail as generatedCommonActivityTemplateDetailActivityTemplateDetailSchema,
    common_activity_template_detail_ActivityTemplateProcessingRequirementsGET as generatedCommonActivityTemplateDetailActivityTemplateProcessingRequirementsGETSchema,
    common_activity_template_detail_ActivityTemplateProcessingRequirements as generatedCommonActivityTemplateDetailActivityTemplateProcessingRequirementsSchema,
    common_activity_template_detail_ActivityTemplateResourceLinkGET as generatedCommonActivityTemplateDetailActivityTemplateResourceLinkGETSchema,
    common_activity_template_detail_ActivityTemplateResourceLink as generatedCommonActivityTemplateDetailActivityTemplateResourceLinkSchema,
    common_activity_template_detail_RequiredActivityTemplatesGET as generatedCommonActivityTemplateDetailRequiredActivityTemplatesGETSchema,
    common_activity_template_detail_RequiredActivityTemplates as generatedCommonActivityTemplateDetailRequiredActivityTemplatesSchema,
    common_activity_template_input_ActivityTemplateInputItemGET as generatedCommonActivityTemplateInputActivityTemplateInputItemGETSchema,
    common_activity_template_output_ActivityTemplateOutputItemGET as generatedCommonActivityTemplateOutputActivityTemplateOutputItemGETSchema,
    common_activity_template_output_TemplateDescriptionGET as generatedCommonActivityTemplateOutputTemplateDescriptionGETSchema,
    common_activity_templates_ActivityTemplatePOST as generatedCommonActivityTemplatesActivityTemplatePOSTSchema,
    common_activity_templates_ActivityTemplateResourceLinkGET as generatedCommonActivityTemplatesActivityTemplateResourceLinkGETSchema,
    common_activity_templates_ActivityTemplateResourceLink as generatedCommonActivityTemplatesActivityTemplateResourceLinkSchema,
    common_activity_templates_ActivityTemplatesGET as generatedCommonActivityTemplatesActivityTemplatesGETSchema,
    common_activity_templates_ActivityTemplates as generatedCommonActivityTemplatesActivityTemplatesSchema,
    common_activity_templates_PrecedenceLinkGET as generatedCommonActivityTemplatesPrecedenceLinkGETSchema,
    common_activity_templates_PrecedenceLink as generatedCommonActivityTemplatesPrecedenceLinkSchema,
    common_batch_templates_with_activity_BatchActivityTemplateWithActivityGET as generatedCommonBatchTemplatesWithActivityBatchActivityTemplateWithActivityGETSchema,
    common_batch_templates_with_activity_ScheduledBatchActivityTemplateWithActivityGET as generatedCommonBatchTemplatesWithActivityScheduledBatchActivityTemplateWithActivityGETSchema,
    common_consumable_resources_ConsumableResourceGET as generatedCommonConsumableResourcesConsumableResourceGETSchema,
    common_consumable_resources_ConsumableResourcePOST as generatedCommonConsumableResourcesConsumableResourcePOSTSchema,
    common_consumable_resources_ConsumableResource as generatedCommonConsumableResourcesConsumableResourceSchema,
    common_consumable_resources_ProjectLinkGET as generatedCommonConsumableResourcesProjectLinkGETSchema,
    common_consumable_resources_ProjectLink as generatedCommonConsumableResourcesProjectLinkSchema,
    common_copy_schedule_CopySchedulePOST as generatedCommonCopyScheduleCopySchedulePOSTSchema,
    common_departments_DepartmentGET as generatedCommonDepartmentsDepartmentGETSchema,
    common_departments_DepartmentPOST as generatedCommonDepartmentsDepartmentPOSTSchema,
    common_departments_Department as generatedCommonDepartmentsDepartmentSchema,
    common_labels_LabelGET as generatedCommonLabelsLabelGETSchema,
    common_labels_LabelMiniGET as generatedCommonLabelsLabelMiniGETSchema,
    common_labels_LabelMini as generatedCommonLabelsLabelMiniSchema,
    common_labels_LabelPOST as generatedCommonLabelsLabelPOSTSchema,
    common_labels_Label as generatedCommonLabelsLabelSchema,
    common_operation_logs_GlobalOperationLogGET as generatedCommonOperationLogsGlobalOperationLogGETSchema,
    common_operation_logs_GlobalOperationLogMiniGET as generatedCommonOperationLogsGlobalOperationLogMiniGETSchema,
    common_operation_logs_GlobalOperationLogMini as generatedCommonOperationLogsGlobalOperationLogMiniSchema,
    common_operation_logs_GlobalOperationLog as generatedCommonOperationLogsGlobalOperationLogSchema,
    common_operation_logs_LogEntryGET as generatedCommonOperationLogsLogEntryGETSchema,
    common_operation_logs_LogEntryRawGET as generatedCommonOperationLogsLogEntryRawGETSchema,
    common_operation_logs_LogEntry as generatedCommonOperationLogsLogEntrySchema,
    common_operation_logs_LogExportTofileGET as generatedCommonOperationLogsLogExportTofileGETSchema,
    common_operation_logs_LogExportTofile as generatedCommonOperationLogsLogExportTofileSchema,
    common_operation_logs_MetadataGET as generatedCommonOperationLogsMetadataGETSchema,
    common_operation_logs_MetadataMiniGET as generatedCommonOperationLogsMetadataMiniGETSchema,
    common_operation_logs_MetadataMini as generatedCommonOperationLogsMetadataMiniSchema,
    common_operation_logs_Metadata as generatedCommonOperationLogsMetadataSchema,
    common_operation_types_OperationTypeGET as generatedCommonOperationTypesOperationTypeGETSchema,
    common_operation_types_OperationType as generatedCommonOperationTypesOperationTypeSchema,
    common_processing_characteristics_ProcessingCharacteristicGET as generatedCommonProcessingCharacteristicsProcessingCharacteristicGETSchema,
    common_processing_characteristics_ProcessingCharacteristicPOST as generatedCommonProcessingCharacteristicsProcessingCharacteristicPOSTSchema,
    common_processing_characteristics_ProcessingCharacteristic as generatedCommonProcessingCharacteristicsProcessingCharacteristicSchema,
    common_production_lines_ProductionLineGET as generatedCommonProductionLinesProductionLineGETSchema,
    common_production_lines_ProductionLinePOST as generatedCommonProductionLinesProductionLinePOSTSchema,
    common_production_lines_ProductionLine as generatedCommonProductionLinesProductionLineSchema,
    common_production_lines_ProductionLineStationGET as generatedCommonProductionLinesProductionLineStationGETSchema,
    common_production_lines_ProductionLineStation as generatedCommonProductionLinesProductionLineStationSchema,
    common_production_types_operation_types_ProductionTypesOperationTypesGET as generatedCommonProductionTypesOperationTypesProductionTypesOperationTypesGETSchema,
    common_production_types_operation_types_ProductionTypesOperationTypes as generatedCommonProductionTypesOperationTypesProductionTypesOperationTypesSchema,
    common_production_types_ProductionTypeGET as generatedCommonProductionTypesProductionTypeGETSchema,
    common_production_types_ProductionType as generatedCommonProductionTypesProductionTypeSchema,
    common_projects_BatchGET as generatedCommonProjectsBatchGETSchema,
    common_projects_Batch as generatedCommonProjectsBatchSchema,
    common_projects_ProjectGET as generatedCommonProjectsProjectGETSchema,
    common_projects_ProjectPOST as generatedCommonProjectsProjectPOSTSchema,
    common_projects_Project as generatedCommonProjectsProjectSchema,
    common_reference_values_ReferenceValueGET as generatedCommonReferenceValuesReferenceValueGETSchema,
    common_schedules_ScheduleGET as generatedCommonSchedulesScheduleGETSchema,
    common_schedules_ScheduleParamsGET as generatedCommonSchedulesScheduleParamsGETSchema,
    common_schedules_ScheduleParams as generatedCommonSchedulesScheduleParamsSchema,
    common_schedules_SchedulePOST as generatedCommonSchedulesSchedulePOSTSchema,
    common_schedules_ScheduleProjectsGET as generatedCommonSchedulesScheduleProjectsGETSchema,
    common_schedules_ScheduleProjects as generatedCommonSchedulesScheduleProjectsSchema,
    common_schedules_Schedule as generatedCommonSchedulesScheduleSchema,
    common_site_locations_SiteLocationGET as generatedCommonSiteLocationsSiteLocationGETSchema,
    common_site_locations_SiteLocationPOST as generatedCommonSiteLocationsSiteLocationPOSTSchema,
    common_site_locations_SiteLocation as generatedCommonSiteLocationsSiteLocationSchema,
    common_station_detail_StationCapabilityGET as generatedCommonStationDetailStationCapabilityGETSchema,
    common_station_detail_StationCapability as generatedCommonStationDetailStationCapabilitySchema,
    common_station_detail_StationDetailGET as generatedCommonStationDetailStationDetailGETSchema,
    common_station_detail_StationDetailPOST as generatedCommonStationDetailStationDetailPOSTSchema,
    common_station_detail_StationDetail as generatedCommonStationDetailStationDetailSchema,
    common_stations_StationGET as generatedCommonStationsStationGETSchema,
    common_stations_StationPOST as generatedCommonStationsStationPOSTSchema,
    common_stations_Station as generatedCommonStationsStationSchema,
    common_station_yard_area_reservations_StationYardAreaReservationGET as generatedCommonStationYardAreaReservationsStationYardAreaReservationGETSchema,
    common_station_yard_area_reservations_StationYardAreaReservation as generatedCommonStationYardAreaReservationsStationYardAreaReservationSchema,
    common_update_activities_Batch as generatedCommonUpdateActivitiesBatchSchema,
    common_update_activities_ResponseActivityMappingGET as generatedCommonUpdateActivitiesResponseActivityMappingGETSchema,
    common_update_activities_ResponseActivityMapping as generatedCommonUpdateActivitiesResponseActivityMappingSchema,
    common_update_activities_UpdateActivityRequest as generatedCommonUpdateActivitiesUpdateActivityRequestSchema,
    common_update_activities_UpdateActivityResponseGET as generatedCommonUpdateActivitiesUpdateActivityResponseGETSchema,
    common_update_activities_UpdateActivityResponse as generatedCommonUpdateActivitiesUpdateActivityResponseSchema,
    common_update_yard_areas_UpdateYardAreaRequest as generatedCommonUpdateYardAreasUpdateYardAreaRequestSchema,
    common_update_yard_areas_UpdateYardAreaResponseGET as generatedCommonUpdateYardAreasUpdateYardAreaResponseGETSchema,
    common_update_yard_areas_UpdateYardAreaResponse as generatedCommonUpdateYardAreasUpdateYardAreaResponseSchema,
    common_yard_areas_AreaStationEntryPointGET as generatedCommonYardAreasAreaStationEntryPointGETSchema,
    common_yard_areas_AreaStationEntryPoint as generatedCommonYardAreasAreaStationEntryPointSchema,
    common_yard_areas_AreaStationGET as generatedCommonYardAreasAreaStationGETSchema,
    common_yard_areas_AreaStationReservationGET as generatedCommonYardAreasAreaStationReservationGETSchema,
    common_yard_areas_AreaStationReservation as generatedCommonYardAreasAreaStationReservationSchema,
    common_yard_areas_AreaStation as generatedCommonYardAreasAreaStationSchema,
    common_yard_areas_EntryPointGET as generatedCommonYardAreasEntryPointGETSchema,
    common_yard_areas_EntryPoint as generatedCommonYardAreasEntryPointSchema,
    common_yard_areas_GeoPointGET as generatedCommonYardAreasGeoPointGETSchema,
    common_yard_areas_GeoPoint as generatedCommonYardAreasGeoPointSchema,
    common_yard_areas_YardAreaForPut as generatedCommonYardAreasYardAreaForPutSchema,
    common_yard_areas_YardAreaGET as generatedCommonYardAreasYardAreaGETSchema,
    common_yard_areas_YardAreaPOST as generatedCommonYardAreasYardAreaPOSTSchema,
    common_yard_areas_YardArea as generatedCommonYardAreasYardAreaSchema,
    data_import_activity_data_ActivityDataImportFormatSchemaGET as generatedDataImportActivityDataActivityDataImportFormatSchemaGETSchema,
    data_import_activity_data_ActivityDataImportFormatSchema as generatedDataImportActivityDataActivityDataImportFormatSchemaSchema,
    data_import_activity_data_ActivityDataImportRequest as generatedDataImportActivityDataActivityDataImportRequestSchema,
    data_import_activity_data_ActivityDataImportResponseGET as generatedDataImportActivityDataActivityDataImportResponseGETSchema,
    data_import_activity_data_ActivityDataImportResponse as generatedDataImportActivityDataActivityDataImportResponseSchema,
    data_import_animation_data_AnimationDataImportRequest as generatedDataImportAnimationDataAnimationDataImportRequestSchema,
    data_import_animation_data_AnimationDataImportResponseGET as generatedDataImportAnimationDataAnimationDataImportResponseGETSchema,
    data_import_animation_data_AnimationDataImportResponse as generatedDataImportAnimationDataAnimationDataImportResponseSchema,
    data_import_formats_ColumnGET as generatedDataImportFormatsColumnGETSchema,
    data_import_formats_Column as generatedDataImportFormatsColumnSchema,
    data_import_formats_ImportDataFormatGET as generatedDataImportFormatsImportDataFormatGETSchema,
    data_import_formats_ImportDataFormat as generatedDataImportFormatsImportDataFormatSchema,
    data_import_future_project_FutureProjectDataImportFormatSchemaGET as generatedDataImportFutureProjectFutureProjectDataImportFormatSchemaGETSchema,
    data_import_future_project_FutureProjectDataImportFormatSchema as generatedDataImportFutureProjectFutureProjectDataImportFormatSchemaSchema,
    data_import_future_project_FutureProjectDataImportRequest as generatedDataImportFutureProjectFutureProjectDataImportRequestSchema,
    data_import_future_project_FutureProjectDataImportResponseGET as generatedDataImportFutureProjectFutureProjectDataImportResponseGETSchema,
    data_import_future_project_FutureProjectDataImportResponse as generatedDataImportFutureProjectFutureProjectDataImportResponseSchema,
    data_import_vpl_planning_data_VPLPlanningDataImportFormatSchemaGET as generatedDataImportVplPlanningDataVPLPlanningDataImportFormatSchemaGETSchema,
    data_import_vpl_planning_data_VPLPlanningDataImportFormatSchema as generatedDataImportVplPlanningDataVPLPlanningDataImportFormatSchemaSchema,
    data_import_vpl_planning_data_VPLPlanningDataImportRequest as generatedDataImportVplPlanningDataVPLPlanningDataImportRequestSchema,
    data_import_vpl_planning_data_VPLPlanningDataImportResponseGET as generatedDataImportVplPlanningDataVPLPlanningDataImportResponseGETSchema,
    data_import_vpl_planning_data_VPLPlanningDataImportResponse as generatedDataImportVplPlanningDataVPLPlanningDataImportResponseSchema,
    Error as generatedErrorSchema,
    filters_activity_templates_ActivityTemplatesFilterGET as generatedFiltersActivityTemplatesActivityTemplatesFilterGETSchema,
    filters_activity_templates_ActivityTemplatesFilter as generatedFiltersActivityTemplatesActivityTemplatesFilterSchema,
    label_types as generatedLabelTypesSchema,
    marshmallow_schema_Schema as generatedMarshmallowSchemaSchemaSchema,
    oplog_web_api_operation_logs_GlobalOperationLogGET as generatedOplogWebApiOperationLogsGlobalOperationLogGETSchema,
    oplog_web_api_operation_logs_GlobalOperationLog as generatedOplogWebApiOperationLogsGlobalOperationLogSchema,
    oplog_web_api_operation_logs_MetadataGET as generatedOplogWebApiOperationLogsMetadataGETSchema,
    oplog_web_api_operation_logs_Metadata as generatedOplogWebApiOperationLogsMetadataSchema,
    oplog_web_api_templates_TemplateGET as generatedOplogWebApiTemplatesTemplateGETSchema,
    oplog_web_api_templates_Template as generatedOplogWebApiTemplatesTemplateSchema,
    project_id__2 as generatedProjectId2Schema,
    project_id as generatedProjectIdSchema,
    schedule_id as generatedScheduleIdSchema,
    station_name as generatedStationNameSchema,
};

export const generatedActivitiesSortSchema = z.object({
    activity_template_external_id: z.string(),
    decisive_end_date: z.string(),
    decisive_start_date: z.string(),
    name: z.string(),
    progress: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    time_delta: z.string(),
});
export const generatedActivityCategoriesSortSchema = z.object({
    name: z.string(),
});
export const generatedActivityCategorizationRulesSortSchema = z.object({
    unknownField: z.string(),
});
export const generatedActivityCategorizationTargetsSortSchema = z.object({
    label: z.string(),
});
export const generatedActivityCategoryGroupsSortSchema = z.object({
    name: z.string(),
});
export const generatedActivityTemplateInputsSortSchema = z.object({
    activity_template_name: z.string(),
    consumable_resource_name: z.string(),
    project_name: z.string(),
});
export const generatedActivityTemplateOutputsSortSchema = z.object({
    consumable_resource_name: z.string(),
    project_name: z.string(),
});
export const generatedActivityTemplatesSortSchema = z.object({
    duration: z.string(),
    external_id: z.string(),
    man_hours: z.string(),
    name: z.string(),
    project_name: z.string(),
});
export const generatedBatchActivityTemplatesWithActivityItemIdSortSchema = z.object({
    decisive_end_date: z.string(),
    decisive_start_date: z.string(),
    duration: z.string(),
    external_id: z.string(),
    man_hours: z.string(),
    name: z.string(),
    progress: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    time_delta: z.string(),
});
export const generatedCalendarPatternsSortSchema = z.object({
    name: z.string(),
    periodicity: z.string(),
});
export const generatedCalendarsSortSchema = z.object({ name: z.string() });
export const generatedConsumableResourcesSortSchema = z.object({
    abbreviation: z.string(),
    external_id: z.string(),
    length: z.string(),
    name: z.string(),
});
export const generatedCountriesSortSchema = z.object({
    code: z.string(),
    name: z.string(),
});
export const generatedDepartmentsSortSchema = z.object({
    external_id: z.string(),
    name: z.string(),
});
export const generatedFiltersActivityTemplatesSortSchema = z.object({
    external_id: z.string(),
    name: z.string(),
    production_type_key: z.string(),
});
export const generatedFiltersLabelsSortSchema = z.object({ name: z.string() });
export const generatedFlowGraphsSortSchema = z.object({
    is_template: z.string(),
    name: z.string(),
    project_name: z.string(),
});
export const generatedFlowNodesSortSchema = z.object({
    is_template: z.string(),
    name: z.string(),
});
export const generatedGraphShapesSortSchema = z.object({
    unknownField: z.string(),
});
export const generatedLabelsSortSchema = z.object({ name: z.string() });
export const generatedLogsSortSchema = z.object({
    activity_key: z.string(),
    end_time: z.string(),
    message: z.string(),
    start_time: z.string(),
    status_code: z.string(),
    username: z.string(),
});
export const generatedLogsItemIdExportSortSchema = z.object({
    code: z.string(),
    level: z.string(),
});
export const generatedLogsItemIdMessagesSortSchema = z.object({
    level: z.string(),
});
export const generatedLogsMiniSortSchema = z.object({
    activity_key: z.string(),
    end_time: z.string(),
    message: z.string(),
    start_time: z.string(),
    status_code: z.string(),
    username: z.string(),
});
export const generatedLogsTemplatesSortSchema = z.object({ code: z.string() });
export const generatedOperationTypesSortSchema = z.object({
    key: z.string(),
    name: z.string(),
});
export const generatedProcessingCharacteristicsSortSchema = z.object({
    key: z.string(),
    name: z.string(),
});
export const generatedProductionLinesSortSchema = z.object({
    abbreviation: z.string(),
    name: z.string(),
});
export const generatedProductionTypesSortSchema = z.object({
    key: z.string(),
    name: z.string(),
});
export const generatedProductionTypesOperationTypesSortSchema = z.object({
    key: z.string(),
    name: z.string(),
});
export const generatedProjectsSortSchema = z.object({
    abbreviation: z.string(),
    due_date: z.string(),
    name: z.string(),
    release_date: z.string(),
});
export const generatedPublicHolidayCalendarsSortSchema = z.object({
    name: z.string(),
});
export const generatedPublicHolidaysSortSchema = z.object({ date: z.string() });
export const generatedReferenceValuesKeySortSchema = z.object({
    name: z.string(),
});
export const generatedSchedulesSortSchema = z.object({
    created_at: z.string(),
    name: z.string(),
});
export const generatedSchedulesScheduleIdActivitiesSortSchema = z.object({
    activity_template_external_id: z.string(),
    decisive_end_date: z.string(),
    decisive_start_date: z.string(),
    name: z.string(),
    progress: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    time_delta: z.string(),
});
export const generatedSchedulesScheduleIdBatchActivityTemplatesWithActivityItemIdSortSchema = z.object({
    decisive_end_date: z.string(),
    decisive_start_date: z.string(),
    duration: z.string(),
    external_id: z.string(),
    man_hours: z.string(),
    name: z.string(),
    progress: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    time_delta: z.string(),
});
export const generatedSiteLocationsSortSchema = z.object({
    external_id: z.string(),
    name: z.string(),
});
export const generatedStationYardAreaReservationsItemIdSortSchema = z.object({
    available_from: z.string(),
    available_to: z.string(),
    project_name: z.string(),
    yard_area_name: z.string(),
});
export const generatedStationsSortSchema = z.object({
    abbreviation: z.string(),
    capacity: z.string(),
    external_id: z.string(),
    name: z.string(),
    production_line_name: z.string(),
});
export const generatedTemplatesSortSchema = z.object({ code: z.string() });
export const generatedYardAreasSortSchema = z.object({
    abbreviation: z.string(),
    name: z.string(),
});
export const generatedYardAreasItemIdSortSchema = z.object({
    unknownField: z.string(),
});

export const generatedActivitiesFilterSchema = z.object({
    name: z.string(),
    activity_template_external_id: z.string(),
    project_name: z.string(),
    production_type_key: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    progress: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedActivityCategoriesFilterSchema = z.object({
    name: z.string(),
    group_id: z.string(),
});
export const generatedActivityCategorizationRulesFilterSchema = z.object({
    category_id: z.string(),
});
export const generatedActivityCategoryGroupsFilterSchema = z.object({
    name: z.string(),
});
export const generatedActivityTemplateInputsFilterSchema = z.object({
    project_name: z.string(),
    project_id: z.string(),
    activity_template_name: z.string(),
    consumable_resource_name: z.string(),
    production_type_key: z.string(),
    search_query: z.string(),
});
export const generatedActivityTemplateOutputsFilterSchema = z.object({
    project_name: z.string(),
    project_id: z.string(),
    consumable_resource_name: z.string(),
    production_type_key: z.string(),
});
export const generatedActivityTemplatesFilterSchema = z.object({
    name: z.string(),
    project_name: z.string(),
    project_id: z.string(),
    external_id: z.string(),
    production_type_id: z.string(),
    production_type_key: z.string(),
    parent_id: z.string(),
    duration: z.string(),
    man_hours: z.string(),
    label_id: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedBatchActivityTemplatesWithActivityItemIdFilterSchema = z.object({
    name: z.string(),
    external_id: z.string(),
    duration: z.string(),
    man_hours: z.string(),
    production_type_key: z.string(),
    station_name: z.string(),
    required_date: z.string(),
    progress: z.string(),
    label_id: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedCalendarPatternsFilterSchema = z.object({
    name: z.string(),
    periodicity: z.string(),
});
export const generatedCalendarsFilterSchema = z.object({
    name: z.string(),
});
export const generatedConsumableResourcesFilterSchema = z.object({
    name: z.string(),
    abbreviation: z.string(),
    production_type_key: z.string(),
    external_id: z.string(),
    length: z.string(),
    project_id: z.string(),
    project_name: z.string(),
});
export const generatedCountriesFilterSchema = z.object({
    name: z.string(),
    code: z.string(),
});
export const generatedDepartmentsFilterSchema = z.object({
    name: z.string(),
    external_id: z.string(),
});
export const generatedFiltersActivityTemplatesFilterSchema = z.object({
    name: z.string(),
    external_id: z.string(),
    production_type_key: z.string(),
    ext_id_name: z.string(),
    project_id: z.string(),
});
export const generatedFiltersLabelsFilterSchema = z.object({
    name: z.string(),
    label_types: z.string(),
    project_id: z.string(),
});
export const generatedFlowGraphsFilterSchema = z.object({
    name: z.string(),
    is_template: z.string(),
    project_name: z.string(),
});
export const generatedFlowNodesFilterSchema = z.object({
    name: z.string(),
    is_template: z.string(),
});
export const generatedGraphShapesFilterSchema = z.object({
    type_key: z.string(),
});
export const generatedLabelsFilterSchema = z.object({
    name: z.string(),
    project_id: z.string(),
    project_name: z.string(),
    label_types: z.string(),
});
export const generatedLogsFilterSchema = z.object({
    activity_key: z.string(),
    end_time: z.string(),
    message: z.string(),
    start_time: z.string(),
    status_code: z.string(),
    username: z.string(),
});
export const generatedLogsItemIdExportFilterSchema = z.object({
    nlevel: z.string(),
    slevel: z.string(),
    format: z.string(),
});
export const generatedLogsItemIdMessagesFilterSchema = z.object({
    nlevel: z.string(),
    slevel: z.string(),
});
export const generatedLogsMiniFilterSchema = z.object({
    activity_key: z.string(),
    end_time: z.string(),
    message: z.string(),
    start_time: z.string(),
    status_code: z.string(),
    username: z.string(),
    project_id: z.string(),
});
export const generatedLogsTemplatesFilterSchema = z.object({
    code: z.string(),
    template: z.string(),
});
export const generatedMaterializedCalendarItemIdFilterSchema = z.object({
    start_date: z.string(),
    end_date: z.string(),
});
export const generatedOperationTypesFilterSchema = z.object({
    name: z.string(),
    key: z.string(),
});
export const generatedProcessingCharacteristicsFilterSchema = z.object({
    name: z.string(),
    key: z.string(),
});
export const generatedProductionLinesFilterSchema = z.object({
    abbreviation: z.string(),
    name: z.string(),
});
export const generatedProductionPlanBatchFilterSchema = z.object({
    station_id: z.string(),
    schedule_id: z.string(),
    timestamp: z.string(),
});
export const generatedProductionTypesFilterSchema = z.object({
    name: z.string(),
    key: z.string(),
});
export const generatedProductionTypesOperationTypesFilterSchema = z.object({
    name: z.string(),
    key: z.string(),
});
export const generatedProjectTimelinesItemIdFilterSchema = z.object({
    highlight_consumable_resource_id: z.string(),
    highlight_activity_template_id: z.string(),
    name: z.string(),
    external_id: z.string(),
    production_type_key: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    progress: z.string(),
    label_id: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedProjectsFilterSchema = z.object({
    name: z.string(),
    is_archived: z.string(),
    due_date: z.string(),
    release_date: z.string(),
});
export const generatedPublicHolidayCalendarsFilterSchema = z.object({
    name: z.string(),
});
export const generatedPublicHolidaysFilterSchema = z.object({
    country: z.string(),
    subdiv: z.string(),
    language: z.string(),
    start: z.string(),
    end: z.string(),
});
export const generatedScheduleChartCategoriesCategoryFilterSchema = z.object({
    category_name: z.string(),
    required_date: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    production_type_key: z.string(),
    name: z.string(),
    project_id: z.string(),
    external_id: z.string(),
    department_ext_id: z.string(),
    station_name: z.string(),
    project_name: z.string(),
    sub_discipline: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    progress: z.string(),
    uniform_tranche_count: z.string(),
    include_archived: z.string(),
});
export const generatedSchedulesFilterSchema = z.object({
    name: z.string(),
    created_at: z.string(),
    project_id: z.string(),
});
export const generatedSchedulesScheduleIdActivitiesFilterSchema = z.object({
    name: z.string(),
    activity_template_external_id: z.string(),
    project_name: z.string(),
    production_type_key: z.string(),
    required_date: z.string(),
    station_name: z.string(),
    progress: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedSchedulesScheduleIdBatchActivityTemplatesWithActivityItemIdFilterSchema = z.object({
    name: z.string(),
    external_id: z.string(),
    duration: z.string(),
    man_hours: z.string(),
    production_type_key: z.string(),
    station_name: z.string(),
    required_date: z.string(),
    progress: z.string(),
    label_id: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    department_ext_id: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    sub_discipline: z.string(),
});
export const generatedSchedulesScheduleIdProjectTimelinesItemIdFilterSchema = z.object({
    batch_id: z.string(),
});
export const generatedSchedulesScheduleIdScheduleChartCategoriesCategoryFilterSchema = z.object({
    category_name: z.string(),
    required_date: z.string(),
    decisive_start_date: z.string(),
    decisive_end_date: z.string(),
    production_type_key: z.string(),
    name: z.string(),
    project_id: z.string(),
    external_id: z.string(),
    department_ext_id: z.string(),
    station_name: z.string(),
    project_name: z.string(),
    sub_discipline: z.string(),
    main_area: z.string(),
    sub_area: z.string(),
    progress: z.string(),
    uniform_tranche_count: z.string(),
    include_archived: z.string(),
    schedule_id: z.string(),
});
export const generatedSiteLocationsFilterSchema = z.object({
    name: z.string(),
    external_id: z.string(),
});
export const generatedStationYardAreaReservationsItemIdFilterSchema = z.object({
    yard_area_name: z.string(),
    available_from: z.string(),
    available_to: z.string(),
    project_name: z.string(),
});
export const generatedStationsFilterSchema = z.object({
    abbreviation: z.string(),
    name: z.string(),
    capacity: z.string(),
    production_line_name: z.string(),
    project_id: z.string(),
    external_id: z.string(),
    operation_type_id: z.string(),
    processing_characteristic_id: z.string(),
});
export const generatedTemplatesFilterSchema = z.object({
    code: z.string(),
    template: z.string(),
});
export const generatedYardAreasFilterSchema = z.object({
    available_from: z.string(),
    available_to: z.string(),
});
export const generatedYardAreasItemIdFilterSchema = z.object({
    available_from: z.string(),
    available_to: z.string(),
});

export type ApiEndpointKeys =
    | "get:/activities"
    | "delete:/activities/:item_id"
    | "get:/activities/:item_id"
    | "put:/activities/:item_id"
    | "get:/activity-categories"
    | "post:/activity-categories"
    | "delete:/activity-categories/:item_id"
    | "get:/activity-categories/:item_id"
    | "put:/activity-categories/:item_id"
    | "post:/activity-categorization/group-rules"
    | "get:/activity-categorization/rules"
    | "post:/activity-categorization/rules"
    | "delete:/activity-categorization/rules/:item_id"
    | "get:/activity-categorization/rules/:item_id"
    | "put:/activity-categorization/rules/:item_id"
    | "get:/activity-categorization/targets"
    | "get:/activity-category-groups"
    | "post:/activity-category-groups"
    | "delete:/activity-category-groups/:item_id"
    | "get:/activity-category-groups/:item_id"
    | "put:/activity-category-groups/:item_id"
    | "get:/activity-relationships/:item_id"
    | "post:/activity-template-detail"
    | "delete:/activity-template-detail/:item_id"
    | "get:/activity-template-detail/:item_id"
    | "put:/activity-template-detail/:item_id"
    | "get:/activity-template-inputs"
    | "get:/activity-template-outputs"
    | "get:/activity-templates"
    | "get:/activity-templates/:item_id"
    | "get:/all-data-scopes"
    | "get:/apidoc.yaml"
    | "get:/auth/"
    | "post:/auth/login"
    | "get:/auth/logout"
    | "post:/auth/logout"
    | "get:/auth/oauth2/authorize/:provider"
    | "get:/auth/oauth2/callback/:provider"
    | "get:/auth/options"
    | "put:/auth/password"
    | "get:/auth/status"
    | "get:/batch-activity-templates-with-activity/:item_id"
    | "get:/calendar-patterns"
    | "post:/calendar-patterns"
    | "delete:/calendar-patterns/:item_id"
    | "get:/calendar-patterns/:item_id"
    | "put:/calendar-patterns/:item_id"
    | "get:/calendars"
    | "post:/calendars"
    | "delete:/calendars/:item_id"
    | "get:/calendars/:item_id"
    | "put:/calendars/:item_id"
    | "get:/config"
    | "get:/consumable-resources"
    | "post:/consumable-resources"
    | "delete:/consumable-resources/:item_id"
    | "get:/consumable-resources/:item_id"
    | "put:/consumable-resources/:item_id"
    | "get:/countries"
    | "get:/current-permissions"
    | "get:/current-user-info"
    | "post:/current-user-info"
    | "get:/data-scope"
    | "put:/data-scope"
    | "get:/data-scopes"
    | "post:/data-scopes"
    | "delete:/data-scopes/:item_id"
    | "get:/data-scopes/:item_id"
    | "put:/data-scopes/:item_id"
    | "get:/departments"
    | "post:/departments"
    | "delete:/departments/:item_id"
    | "get:/departments/:item_id"
    | "put:/departments/:item_id"
    | "get:/filters/activity-templates"
    | "get:/filters/activity-templates/:item_id"
    | "get:/filters/labels"
    | "get:/filters/labels/:item_id"
    | "get:/flow-graphs"
    | "post:/flow-graphs"
    | "delete:/flow-graphs/:item_id"
    | "get:/flow-graphs/:item_id"
    | "put:/flow-graphs/:item_id"
    | "get:/flow-nodes"
    | "post:/flow-nodes"
    | "delete:/flow-nodes/:item_id"
    | "get:/flow-nodes/:item_id"
    | "put:/flow-nodes/:item_id"
    | "get:/graph-shapes"
    | "post:/graph-shapes"
    | "delete:/graph-shapes/:item_id"
    | "get:/graph-shapes/:item_id"
    | "put:/graph-shapes/:item_id"
    | "post:/import/activity-data"
    | "get:/import/activity-data/format"
    | "post:/import/animation-data"
    | "get:/import/animation-data/format"
    | "post:/import/future-project"
    | "get:/import/future-project/format"
    | "post:/import/vpl-planning-data"
    | "get:/import/vpl-planning-data/format"
    | "get:/labels"
    | "post:/labels"
    | "delete:/labels/:item_id"
    | "get:/labels/:item_id"
    | "put:/labels/:item_id"
    | "get:/logs"
    | "get:/logs/:item_id"
    | "get:/logs/:item_id/export"
    | "get:/logs/:item_id/messages"
    | "get:/logs/mini"
    | "get:/logs/templates"
    | "get:/materialized-calendar/:item_id"
    | "get:/operation-types"
    | "post:/operation-types"
    | "get:/processing-characteristics"
    | "post:/processing-characteristics"
    | "delete:/processing-characteristics/:item_id"
    | "get:/processing-characteristics/:item_id"
    | "put:/processing-characteristics/:item_id"
    | "get:/production-lines"
    | "post:/production-lines"
    | "delete:/production-lines/:item_id"
    | "get:/production-lines/:item_id"
    | "put:/production-lines/:item_id"
    | "get:/production-plan-batch"
    | "get:/production-types"
    | "get:/production-types-operation-types"
    | "get:/production-types-operation-types/:item_id"
    | "get:/project-timelines/:item_id"
    | "get:/projects"
    | "post:/projects"
    | "delete:/projects/:item_id"
    | "get:/projects/:item_id"
    | "put:/projects/:item_id"
    | "get:/public-holiday-calendars"
    | "post:/public-holiday-calendars"
    | "delete:/public-holiday-calendars/:item_id"
    | "get:/public-holiday-calendars/:item_id"
    | "put:/public-holiday-calendars/:item_id"
    | "get:/public-holidays"
    | "get:/reference-values/:key"
    | "put:/resend-activation-email/:user_id"
    | "post:/reset-password"
    | "get:/roles"
    | "post:/roles"
    | "delete:/roles/:role_id"
    | "get:/roles/:role_id"
    | "put:/roles/:role_id"
    | "get:/schedule-activities"
    | "post:/schedule-activities"
    | "get:/schedule-activities/:item_id"
    | "post:/schedule-assembly-activities"
    | "get:/schedule-chart-categories/:category"
    | "get:/schedule-production-lines"
    | "post:/schedule-production-lines"
    | "get:/schedule-production-lines/:item_id"
    | "get:/schedules"
    | "post:/schedules"
    | "delete:/schedules/:item_id"
    | "get:/schedules/:item_id"
    | "put:/schedules/:item_id"
    | "get:/schedules/:schedule_id/activities"
    | "get:/schedules/:schedule_id/activities/:item_id"
    | "put:/schedules/:schedule_id/activities/:item_id"
    | "get:/schedules/:schedule_id/activity-relationships/:item_id"
    | "get:/schedules/:schedule_id/batch-activity-templates-with-activity/:item_id"
    | "get:/schedules/:schedule_id/project-timelines/:item_id"
    | "get:/schedules/:schedule_id/schedule-chart-categories/:category"
    | "post:/schedules/copy"
    | "get:/site-locations"
    | "post:/site-locations"
    | "delete:/site-locations/:item_id"
    | "get:/site-locations/:item_id"
    | "put:/site-locations/:item_id"
    | "post:/station-detail"
    | "delete:/station-detail/:item_id"
    | "get:/station-detail/:item_id"
    | "put:/station-detail/:item_id"
    | "get:/station-yard-area-reservations/:item_id"
    | "get:/stations"
    | "post:/stations"
    | "delete:/stations/:item_id"
    | "get:/stations/:item_id"
    | "put:/stations/:item_id"
    | "get:/templates"
    | "post:/update-activities"
    | "post:/update-yard-areas"
    | "get:/user-settings"
    | "put:/user-settings"
    | "get:/users"
    | "post:/users"
    | "get:/users/:user_id"
    | "put:/users/:user_id"
    | "get:/version"
    | "get:/visualize-geometry"
    | "post:/visualize-geometry"
    | "get:/visualize-geometry/files/:project_id/:file_name"
    | "get:/yard-areas"
    | "post:/yard-areas"
    | "delete:/yard-areas/:item_id"
    | "get:/yard-areas/:item_id"
    | "put:/yard-areas/:item_id";

const endpointSchema = z.object({
    endpoint: z.string(),
    method: z.enum(["GET", "POST", "PUT", "DELETE"]),
});

export const apiEndpoints: Record<ApiEndpointKeys, z.infer<typeof endpointSchema>> = {
    "get:/activities": {
        method: "GET",
        endpoint: "/activities",
    },
    "delete:/activities/:item_id": {
        method: "DELETE",
        endpoint: "/activities/<item_id>",
    },
    "get:/activities/:item_id": {
        method: "GET",
        endpoint: "/activities/<item_id>",
    },
    "put:/activities/:item_id": {
        method: "PUT",
        endpoint: "/activities/<item_id>",
    },
    "get:/activity-categories": {
        method: "GET",
        endpoint: "/activity-categories",
    },
    "post:/activity-categories": {
        method: "POST",
        endpoint: "/activity-categories",
    },
    "delete:/activity-categories/:item_id": {
        method: "DELETE",
        endpoint: "/activity-categories/<item_id>",
    },
    "get:/activity-categories/:item_id": {
        method: "GET",
        endpoint: "/activity-categories/<item_id>",
    },
    "put:/activity-categories/:item_id": {
        method: "PUT",
        endpoint: "/activity-categories/<item_id>",
    },
    "post:/activity-categorization/group-rules": {
        method: "POST",
        endpoint: "/activity-categorization/group-rules",
    },
    "get:/activity-categorization/rules": {
        method: "GET",
        endpoint: "/activity-categorization/rules",
    },
    "post:/activity-categorization/rules": {
        method: "POST",
        endpoint: "/activity-categorization/rules",
    },
    "delete:/activity-categorization/rules/:item_id": {
        method: "DELETE",
        endpoint: "/activity-categorization/rules/<item_id>",
    },
    "get:/activity-categorization/rules/:item_id": {
        method: "GET",
        endpoint: "/activity-categorization/rules/<item_id>",
    },
    "put:/activity-categorization/rules/:item_id": {
        method: "PUT",
        endpoint: "/activity-categorization/rules/<item_id>",
    },
    "get:/activity-categorization/targets": {
        method: "GET",
        endpoint: "/activity-categorization/targets",
    },
    "get:/activity-category-groups": {
        method: "GET",
        endpoint: "/activity-category-groups",
    },
    "post:/activity-category-groups": {
        method: "POST",
        endpoint: "/activity-category-groups",
    },
    "delete:/activity-category-groups/:item_id": {
        method: "DELETE",
        endpoint: "/activity-category-groups/<item_id>",
    },
    "get:/activity-category-groups/:item_id": {
        method: "GET",
        endpoint: "/activity-category-groups/<item_id>",
    },
    "put:/activity-category-groups/:item_id": {
        method: "PUT",
        endpoint: "/activity-category-groups/<item_id>",
    },
    "get:/activity-relationships/:item_id": {
        method: "GET",
        endpoint: "/activity-relationships/<item_id>",
    },
    "post:/activity-template-detail": {
        method: "POST",
        endpoint: "/activity-template-detail",
    },
    "delete:/activity-template-detail/:item_id": {
        method: "DELETE",
        endpoint: "/activity-template-detail/<item_id>",
    },
    "get:/activity-template-detail/:item_id": {
        method: "GET",
        endpoint: "/activity-template-detail/<item_id>",
    },
    "put:/activity-template-detail/:item_id": {
        method: "PUT",
        endpoint: "/activity-template-detail/<item_id>",
    },
    "get:/activity-template-inputs": {
        method: "GET",
        endpoint: "/activity-template-inputs",
    },
    "get:/activity-template-outputs": {
        method: "GET",
        endpoint: "/activity-template-outputs",
    },
    "get:/activity-templates": {
        method: "GET",
        endpoint: "/activity-templates",
    },
    "get:/activity-templates/:item_id": {
        method: "GET",
        endpoint: "/activity-templates/<item_id>",
    },
    "get:/all-data-scopes": {
        method: "GET",
        endpoint: "/all-data-scopes",
    },
    "get:/apidoc.yaml": {
        method: "GET",
        endpoint: "/apidoc.yaml",
    },
    "get:/auth/": {
        method: "GET",
        endpoint: "/auth",
    },
    "post:/auth/login": {
        method: "POST",
        endpoint: "/auth/login",
    },
    "get:/auth/logout": {
        method: "GET",
        endpoint: "/auth/logout",
    },
    "post:/auth/logout": {
        method: "POST",
        endpoint: "/auth/logout",
    },
    "get:/auth/oauth2/authorize/:provider": {
        method: "GET",
        endpoint: "/auth/oauth2/authorize/<provider>",
    },
    "get:/auth/oauth2/callback/:provider": {
        method: "GET",
        endpoint: "/auth/oauth2/callback/<provider>",
    },
    "get:/auth/options": {
        method: "GET",
        endpoint: "/auth/options",
    },
    "put:/auth/password": {
        method: "PUT",
        endpoint: "/auth/password",
    },
    "get:/auth/status": {
        method: "GET",
        endpoint: "/auth/status",
    },
    "get:/batch-activity-templates-with-activity/:item_id": {
        method: "GET",
        endpoint: "/batch-activity-templates-with-activity/<item_id>",
    },
    "get:/calendar-patterns": {
        method: "GET",
        endpoint: "/calendar-patterns",
    },
    "post:/calendar-patterns": {
        method: "POST",
        endpoint: "/calendar-patterns",
    },
    "delete:/calendar-patterns/:item_id": {
        method: "DELETE",
        endpoint: "/calendar-patterns/<item_id>",
    },
    "get:/calendar-patterns/:item_id": {
        method: "GET",
        endpoint: "/calendar-patterns/<item_id>",
    },
    "put:/calendar-patterns/:item_id": {
        method: "PUT",
        endpoint: "/calendar-patterns/<item_id>",
    },
    "get:/calendars": {
        method: "GET",
        endpoint: "/calendars",
    },
    "post:/calendars": {
        method: "POST",
        endpoint: "/calendars",
    },
    "delete:/calendars/:item_id": {
        method: "DELETE",
        endpoint: "/calendars/<item_id>",
    },
    "get:/calendars/:item_id": {
        method: "GET",
        endpoint: "/calendars/<item_id>",
    },
    "put:/calendars/:item_id": {
        method: "PUT",
        endpoint: "/calendars/<item_id>",
    },
    "get:/config": {
        method: "GET",
        endpoint: "/config",
    },
    "get:/consumable-resources": {
        method: "GET",
        endpoint: "/consumable-resources",
    },
    "post:/consumable-resources": {
        method: "POST",
        endpoint: "/consumable-resources",
    },
    "delete:/consumable-resources/:item_id": {
        method: "DELETE",
        endpoint: "/consumable-resources/<item_id>",
    },
    "get:/consumable-resources/:item_id": {
        method: "GET",
        endpoint: "/consumable-resources/<item_id>",
    },
    "put:/consumable-resources/:item_id": {
        method: "PUT",
        endpoint: "/consumable-resources/<item_id>",
    },
    "get:/countries": {
        method: "GET",
        endpoint: "/countries",
    },
    "get:/current-permissions": {
        method: "GET",
        endpoint: "/current-permissions",
    },
    "get:/current-user-info": {
        method: "GET",
        endpoint: "/current-user-info",
    },
    "post:/current-user-info": {
        method: "POST",
        endpoint: "/current-user-info",
    },
    "get:/data-scope": {
        method: "GET",
        endpoint: "/data-scope",
    },
    "put:/data-scope": {
        method: "PUT",
        endpoint: "/data-scope",
    },
    "get:/data-scopes": {
        method: "GET",
        endpoint: "/data-scopes",
    },
    "post:/data-scopes": {
        method: "POST",
        endpoint: "/data-scopes",
    },
    "delete:/data-scopes/:item_id": {
        method: "DELETE",
        endpoint: "/data-scopes/<item_id>",
    },
    "get:/data-scopes/:item_id": {
        method: "GET",
        endpoint: "/data-scopes/<item_id>",
    },
    "put:/data-scopes/:item_id": {
        method: "PUT",
        endpoint: "/data-scopes/<item_id>",
    },
    "get:/departments": {
        method: "GET",
        endpoint: "/departments",
    },
    "post:/departments": {
        method: "POST",
        endpoint: "/departments",
    },
    "delete:/departments/:item_id": {
        method: "DELETE",
        endpoint: "/departments/<item_id>",
    },
    "get:/departments/:item_id": {
        method: "GET",
        endpoint: "/departments/<item_id>",
    },
    "put:/departments/:item_id": {
        method: "PUT",
        endpoint: "/departments/<item_id>",
    },
    "get:/filters/activity-templates": {
        method: "GET",
        endpoint: "/filters/activity-templates",
    },
    "get:/filters/activity-templates/:item_id": {
        method: "GET",
        endpoint: "/filters/activity-templates/<item_id>",
    },
    "get:/filters/labels": {
        method: "GET",
        endpoint: "/filters/labels",
    },
    "get:/filters/labels/:item_id": {
        method: "GET",
        endpoint: "/filters/labels/<item_id>",
    },
    "get:/flow-graphs": {
        method: "GET",
        endpoint: "/flow-graphs",
    },
    "post:/flow-graphs": {
        method: "POST",
        endpoint: "/flow-graphs",
    },
    "delete:/flow-graphs/:item_id": {
        method: "DELETE",
        endpoint: "/flow-graphs/<item_id>",
    },
    "get:/flow-graphs/:item_id": {
        method: "GET",
        endpoint: "/flow-graphs/<item_id>",
    },
    "put:/flow-graphs/:item_id": {
        method: "PUT",
        endpoint: "/flow-graphs/<item_id>",
    },
    "get:/flow-nodes": {
        method: "GET",
        endpoint: "/flow-nodes",
    },
    "post:/flow-nodes": {
        method: "POST",
        endpoint: "/flow-nodes",
    },
    "delete:/flow-nodes/:item_id": {
        method: "DELETE",
        endpoint: "/flow-nodes/<item_id>",
    },
    "get:/flow-nodes/:item_id": {
        method: "GET",
        endpoint: "/flow-nodes/<item_id>",
    },
    "put:/flow-nodes/:item_id": {
        method: "PUT",
        endpoint: "/flow-nodes/<item_id>",
    },
    "get:/graph-shapes": {
        method: "GET",
        endpoint: "/graph-shapes",
    },
    "post:/graph-shapes": {
        method: "POST",
        endpoint: "/graph-shapes",
    },
    "delete:/graph-shapes/:item_id": {
        method: "DELETE",
        endpoint: "/graph-shapes/<item_id>",
    },
    "get:/graph-shapes/:item_id": {
        method: "GET",
        endpoint: "/graph-shapes/<item_id>",
    },
    "put:/graph-shapes/:item_id": {
        method: "PUT",
        endpoint: "/graph-shapes/<item_id>",
    },
    "post:/import/activity-data": {
        method: "POST",
        endpoint: "/import/activity-data",
    },
    "get:/import/activity-data/format": {
        method: "GET",
        endpoint: "/import/activity-data/format",
    },
    "post:/import/animation-data": {
        method: "POST",
        endpoint: "/import/animation-data",
    },
    "get:/import/animation-data/format": {
        method: "GET",
        endpoint: "/import/animation-data/format",
    },
    "post:/import/future-project": {
        method: "POST",
        endpoint: "/import/future-project",
    },
    "get:/import/future-project/format": {
        method: "GET",
        endpoint: "/import/future-project/format",
    },
    "post:/import/vpl-planning-data": {
        method: "POST",
        endpoint: "/import/vpl-planning-data",
    },
    "get:/import/vpl-planning-data/format": {
        method: "GET",
        endpoint: "/import/vpl-planning-data/format",
    },
    "get:/labels": {
        method: "GET",
        endpoint: "/labels",
    },
    "post:/labels": {
        method: "POST",
        endpoint: "/labels",
    },
    "delete:/labels/:item_id": {
        method: "DELETE",
        endpoint: "/labels/<item_id>",
    },
    "get:/labels/:item_id": {
        method: "GET",
        endpoint: "/labels/<item_id>",
    },
    "put:/labels/:item_id": {
        method: "PUT",
        endpoint: "/labels/<item_id>",
    },
    "get:/logs": {
        method: "GET",
        endpoint: "/logs",
    },
    "get:/logs/:item_id": {
        method: "GET",
        endpoint: "/logs/<item_id>",
    },
    "get:/logs/:item_id/export": {
        method: "GET",
        endpoint: "/logs/<item_id>/export",
    },
    "get:/logs/:item_id/messages": {
        method: "GET",
        endpoint: "/logs/<item_id>/messages",
    },
    "get:/logs/mini": {
        method: "GET",
        endpoint: "/logs/mini",
    },
    "get:/logs/templates": {
        method: "GET",
        endpoint: "/logs/templates",
    },
    "get:/materialized-calendar/:item_id": {
        method: "GET",
        endpoint: "/materialized-calendar/<item_id>",
    },
    "get:/operation-types": {
        method: "GET",
        endpoint: "/operation-types",
    },
    "post:/operation-types": {
        method: "POST",
        endpoint: "/operation-types",
    },
    "get:/processing-characteristics": {
        method: "GET",
        endpoint: "/processing-characteristics",
    },
    "post:/processing-characteristics": {
        method: "POST",
        endpoint: "/processing-characteristics",
    },
    "delete:/processing-characteristics/:item_id": {
        method: "DELETE",
        endpoint: "/processing-characteristics/<item_id>",
    },
    "get:/processing-characteristics/:item_id": {
        method: "GET",
        endpoint: "/processing-characteristics/<item_id>",
    },
    "put:/processing-characteristics/:item_id": {
        method: "PUT",
        endpoint: "/processing-characteristics/<item_id>",
    },
    "get:/production-lines": {
        method: "GET",
        endpoint: "/production-lines",
    },
    "post:/production-lines": {
        method: "POST",
        endpoint: "/production-lines",
    },
    "delete:/production-lines/:item_id": {
        method: "DELETE",
        endpoint: "/production-lines/<item_id>",
    },
    "get:/production-lines/:item_id": {
        method: "GET",
        endpoint: "/production-lines/<item_id>",
    },
    "put:/production-lines/:item_id": {
        method: "PUT",
        endpoint: "/production-lines/<item_id>",
    },
    "get:/production-plan-batch": {
        method: "GET",
        endpoint: "/production-plan-batch",
    },
    "get:/production-types": {
        method: "GET",
        endpoint: "/production-types",
    },
    "get:/production-types-operation-types": {
        method: "GET",
        endpoint: "/production-types-operation-types",
    },
    "get:/production-types-operation-types/:item_id": {
        method: "GET",
        endpoint: "/production-types-operation-types/<item_id>",
    },
    "get:/project-timelines/:item_id": {
        method: "GET",
        endpoint: "/project-timelines/<item_id>",
    },
    "get:/projects": {
        method: "GET",
        endpoint: "/projects",
    },
    "post:/projects": {
        method: "POST",
        endpoint: "/projects",
    },
    "delete:/projects/:item_id": {
        method: "DELETE",
        endpoint: "/projects/<item_id>",
    },
    "get:/projects/:item_id": {
        method: "GET",
        endpoint: "/projects/<item_id>",
    },
    "put:/projects/:item_id": {
        method: "PUT",
        endpoint: "/projects/<item_id>",
    },
    "get:/public-holiday-calendars": {
        method: "GET",
        endpoint: "/public-holiday-calendars",
    },
    "post:/public-holiday-calendars": {
        method: "POST",
        endpoint: "/public-holiday-calendars",
    },
    "delete:/public-holiday-calendars/:item_id": {
        method: "DELETE",
        endpoint: "/public-holiday-calendars/<item_id>",
    },
    "get:/public-holiday-calendars/:item_id": {
        method: "GET",
        endpoint: "/public-holiday-calendars/<item_id>",
    },
    "put:/public-holiday-calendars/:item_id": {
        method: "PUT",
        endpoint: "/public-holiday-calendars/<item_id>",
    },
    "get:/public-holidays": {
        method: "GET",
        endpoint: "/public-holidays",
    },
    "get:/reference-values/:key": {
        method: "GET",
        endpoint: "/reference-values/<key>",
    },
    "put:/resend-activation-email/:user_id": {
        method: "PUT",
        endpoint: "/resend-activation-email/<user_id>",
    },
    "post:/reset-password": {
        method: "POST",
        endpoint: "/reset-password",
    },
    "get:/roles": {
        method: "GET",
        endpoint: "/roles",
    },
    "post:/roles": {
        method: "POST",
        endpoint: "/roles",
    },
    "delete:/roles/:role_id": {
        method: "DELETE",
        endpoint: "/roles/<role_id>",
    },
    "get:/roles/:role_id": {
        method: "GET",
        endpoint: "/roles/<role_id>",
    },
    "put:/roles/:role_id": {
        method: "PUT",
        endpoint: "/roles/<role_id>",
    },
    "get:/schedule-activities": {
        method: "GET",
        endpoint: "/schedule-activities",
    },
    "post:/schedule-activities": {
        method: "POST",
        endpoint: "/schedule-activities",
    },
    "get:/schedule-activities/:item_id": {
        method: "GET",
        endpoint: "/schedule-activities/<item_id>",
    },
    "post:/schedule-assembly-activities": {
        method: "POST",
        endpoint: "/schedule-assembly-activities",
    },
    "get:/schedule-chart-categories/:category": {
        method: "GET",
        endpoint: "/schedule-chart-categories/<category>",
    },
    "get:/schedule-production-lines": {
        method: "GET",
        endpoint: "/schedule-production-lines",
    },
    "post:/schedule-production-lines": {
        method: "POST",
        endpoint: "/schedule-production-lines",
    },
    "get:/schedule-production-lines/:item_id": {
        method: "GET",
        endpoint: "/schedule-production-lines/<item_id>",
    },
    "get:/schedules": {
        method: "GET",
        endpoint: "/schedules",
    },
    "post:/schedules": {
        method: "POST",
        endpoint: "/schedules",
    },
    "delete:/schedules/:item_id": {
        method: "DELETE",
        endpoint: "/schedules/<item_id>",
    },
    "get:/schedules/:item_id": {
        method: "GET",
        endpoint: "/schedules/<item_id>",
    },
    "put:/schedules/:item_id": {
        method: "PUT",
        endpoint: "/schedules/<item_id>",
    },
    "get:/schedules/:schedule_id/activities": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/activities",
    },
    "get:/schedules/:schedule_id/activities/:item_id": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/activities/<item_id>",
    },
    "put:/schedules/:schedule_id/activities/:item_id": {
        method: "PUT",
        endpoint: "/schedules/<schedule_id>/activities/<item_id>",
    },
    "get:/schedules/:schedule_id/activity-relationships/:item_id": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/activity-relationships/<item_id>",
    },
    "get:/schedules/:schedule_id/batch-activity-templates-with-activity/:item_id": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/batch-activity-templates-with-activity/<item_id>",
    },
    "get:/schedules/:schedule_id/project-timelines/:item_id": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/project-timelines/<item_id>",
    },
    "get:/schedules/:schedule_id/schedule-chart-categories/:category": {
        method: "GET",
        endpoint: "/schedules/<schedule_id>/schedule-chart-categories/<category>",
    },
    "post:/schedules/copy": {
        method: "POST",
        endpoint: "/schedules/copy",
    },
    "get:/site-locations": {
        method: "GET",
        endpoint: "/site-locations",
    },
    "post:/site-locations": {
        method: "POST",
        endpoint: "/site-locations",
    },
    "delete:/site-locations/:item_id": {
        method: "DELETE",
        endpoint: "/site-locations/<item_id>",
    },
    "get:/site-locations/:item_id": {
        method: "GET",
        endpoint: "/site-locations/<item_id>",
    },
    "put:/site-locations/:item_id": {
        method: "PUT",
        endpoint: "/site-locations/<item_id>",
    },
    "post:/station-detail": {
        method: "POST",
        endpoint: "/station-detail",
    },
    "delete:/station-detail/:item_id": {
        method: "DELETE",
        endpoint: "/station-detail/<item_id>",
    },
    "get:/station-detail/:item_id": {
        method: "GET",
        endpoint: "/station-detail/<item_id>",
    },
    "put:/station-detail/:item_id": {
        method: "PUT",
        endpoint: "/station-detail/<item_id>",
    },
    "get:/station-yard-area-reservations/:item_id": {
        method: "GET",
        endpoint: "/station-yard-area-reservations/<item_id>",
    },
    "get:/stations": {
        method: "GET",
        endpoint: "/stations",
    },
    "post:/stations": {
        method: "POST",
        endpoint: "/stations",
    },
    "delete:/stations/:item_id": {
        method: "DELETE",
        endpoint: "/stations/<item_id>",
    },
    "get:/stations/:item_id": {
        method: "GET",
        endpoint: "/stations/<item_id>",
    },
    "put:/stations/:item_id": {
        method: "PUT",
        endpoint: "/stations/<item_id>",
    },
    "get:/templates": {
        method: "GET",
        endpoint: "/templates",
    },
    "post:/update-activities": {
        method: "POST",
        endpoint: "/update-activities",
    },
    "post:/update-yard-areas": {
        method: "POST",
        endpoint: "/update-yard-areas",
    },
    "get:/user-settings": {
        method: "GET",
        endpoint: "/user-settings",
    },
    "put:/user-settings": {
        method: "PUT",
        endpoint: "/user-settings",
    },
    "get:/users": {
        method: "GET",
        endpoint: "/users",
    },
    "post:/users": {
        method: "POST",
        endpoint: "/users",
    },
    "get:/users/:user_id": {
        method: "GET",
        endpoint: "/users/<user_id>",
    },
    "put:/users/:user_id": {
        method: "PUT",
        endpoint: "/users/<user_id>",
    },
    "get:/version": {
        method: "GET",
        endpoint: "/version",
    },
    "get:/visualize-geometry": {
        method: "GET",
        endpoint: "/visualize-geometry",
    },
    "post:/visualize-geometry": {
        method: "POST",
        endpoint: "/visualize-geometry",
    },
    "get:/visualize-geometry/files/:project_id/:file_name": {
        method: "GET",
        endpoint: "/visualize-geometry/files/<project_id>/<file_name>",
    },
    "get:/yard-areas": {
        method: "GET",
        endpoint: "/yard-areas",
    },
    "post:/yard-areas": {
        method: "POST",
        endpoint: "/yard-areas",
    },
    "delete:/yard-areas/:item_id": {
        method: "DELETE",
        endpoint: "/yard-areas/<item_id>",
    },
    "get:/yard-areas/:item_id": {
        method: "GET",
        endpoint: "/yard-areas/<item_id>",
    },
    "put:/yard-areas/:item_id": {
        method: "PUT",
        endpoint: "/yard-areas/<item_id>",
    },
};
